import { BillCandidateEvidence, billCandidateSource } from "@/components/customer/BillCandidateEvidence";
import { BillExtractionProgress, billProgressRefetchInterval } from "@/components/customer/BillExtractionProgress";
import {
  CommercialPinMap,
  type Pin,
} from "@/components/customer/CommercialPinMap";
import { CommercialRoofSurvey } from "@/components/customer/CommercialRoofSurvey";
import { resolveCartoCiudadCandidate, searchCartoCiudad, type CartoCiudadCandidate, type ResolvedCartoCiudadCandidate } from "@/lib/cartoCiudadGeocoder";
import { CommercialDocumentManager } from "@/components/customer/CommercialDocumentManager";
import { EvEnergyStudyPanel } from "@/components/commercial/EvEnergyStudyPanel";
import { commercialUploadErrorMessage, commercialUploadHttpError, commercialUploadPartialMessage, commercialUploadReadbackMessage } from "@/lib/commercialUploadErrors";
import { buildCommercialBillProposalBatch, commercialBillCandidateValue } from "@/lib/commercialBillProposal";
import { commercialPostalLocationProposal, commercialSupplyLocationSeed } from "@/lib/commercialLocation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { reconcileEvCatalogOption, resolveEvCatalogOption } from "@/features/evSales/selection";
import {
  COMMERCIAL_OBJECTIVE_LABELS,
  COMMERCIAL_PHOTO_CATEGORY_LABELS,
  FUTURE_NEED_LABELS,
  formatConsumptionPattern,
  formatFutureNeedStatus,
  COMMERCIAL_SERVICE_LABELS,
  commercialPendingItems,
  deriveCompleteMonthlyTotals,
  hasNewPvHolderContractorDifference,
  isGeoConfirmationCurrent,
  mergeAppliedBillReadback,
  mergeAppliedBillLocationReadback,
} from "@/lib/commercialWizardSummary";
import {
  hasCommercialSubmissionContactDetails,
  hasCommercialSubmissionContactMethod,
  hasCommercialSubmissionIdentity,
  isValidCommercialEmail,
  isValidCommercialPhone,
} from "../../../shared/commercial/submissionReadiness";
import {
  commercialWizardStages,
  persistedCommercialWizardStep,
  restoreCommercialWizardStep,
} from "../../../shared/commercial/wizardStages";
import { useAuth } from "@/_core/hooks/useAuth";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";

type Service = "photovoltaic" | "ev_charger" | "maintenance";

function CommercialPresentationFrame({ children, headerContent }: { children: React.ReactNode; headerContent?: React.ReactNode }) {
  const variant = new URLSearchParams(window.location.search).get("commercialVariant") === "dark" ? "dark" : "balanced";
  return (
    <div className="commercial-presentation min-h-screen bg-background text-foreground" data-commercial-variant={variant}>
      <header className="commercial-presentation-header">
        {headerContent ?? <div className="commercial-presentation-brand mx-auto flex w-full max-w-[1280px] items-center gap-3 px-4 py-3 sm:px-5 md:px-7">
          <img src="/brand/ods-logo-commercial-white.png" alt="ODS Energy" className="commercial-logo-white h-8 w-auto max-w-[150px] object-contain" />
          <span className="text-sm font-medium">Solicitud comercial</span>
        </div>}
      </header>
      <div className="commercial-presentation-content">{children}</div>
    </div>
  );
}
function commercialBillReviewErrorMessage(error: unknown, action: "field" | "batch" = "field"): string {
  const code = (error as { message?: unknown } | null)?.message;
  if (code === "COMMERCIAL_REQUEST_VERSION_CONFLICT") return "No se guardaron los cambios: el borrador cambió en otra edición. Recarga y revisa los datos antes de continuar.";
  if (code === "BILL_FIELD_CONFLICT") return "No se aplicó el lote: ya hay datos en uno o más campos. Revísalos individualmente antes de sustituirlos.";
  if (code === "BILL_BATCH_VERSION_CONFLICT") return "No se aplicó el lote: el borrador cambió en otra edición. Recarga y vuelve a revisar.";
  if (code === "BILL_READING_PERIOD_REQUIRED")
    return "No se aplicó ninguna lectura: indica periodos explícitos, por ejemplo P1: 4,6 kW o P1: 120 kWh, uno por línea.";
  if (code === "BILL_READING_INTERVAL_INVALID")
    return "No se aplicó la lectura: las fechas del intervalo son inválidas o están invertidas. Corrígelas y vuelve a revisar.";
  if (code === "BILL_SUPPLY_SELECTION_REQUIRED")
    return "Selecciona el suministro de destino antes de aplicar estas lecturas.";
  if (code === "BILL_SUPPLY_NOT_IN_REQUEST" || code === "BILL_DOCUMENT_SUPPLY_MISMATCH" || code === "BILL_CAPTURE_GROUP_SUPPLY_MISMATCH")
    return "No se aplicó la lectura porque el documento no corresponde al suministro seleccionado. Revisa la asociación del documento.";
  if (code === "BILL_PERIOD_TARIFF_CONFLICT")
    return "No se aplicó la lectura: el periodo no corresponde a la tarifa del suministro.";
  if (action === "batch") return "No se pudo aplicar el lote de factura al suministro. Reintenta Aplicar todo con los mismos datos; si persiste, recarga y revisa el borrador antes de continuar.";
  return "No se pudo revisar el candidato OCR. Comprueba los datos e inténtalo de nuevo.";
}
type Draft = {
  serviceType: Service | null;
  source:
    | "pending"
    | "recommendation"
    | "web"
    | "advertising"
    | "direct"
    | "other";
  customerPartnerId: number | null;
  contactId: number | null;
  siteId: number | null;
  need: Record<string, any>;
  parties: Record<string, any>;
  location: Record<string, any>;
};
const emptyDraft = (): Draft => ({
  serviceType: null,
  source: "pending",
  customerPartnerId: null,
  contactId: null,
  siteId: null,
  need: {},
  parties: {},
  location: {},
});
const photovoltaicPhotoCategories = [
  ["roof_overview", "Vista general del tejado"],
  ["obstacles", "Obstáculos y sombras"],
  ["electrical_panel", "Cuadro eléctrico"],
  ["inverter", "Ubicación del inversor"],
  ["cable_route", "Recorrido del cableado"],
  ["existing_equipment", "Equipos, etiquetas y accesos"],
] as const;
const evPhotoCategories = [
  ["electrical_panel", "Cuadro eléctrico"],
  ["cable_route", "Recorrido del cableado"],
  ["existing_equipment", "Equipo y espacio de instalación"],
  ["other", "Otra fotografía"],
] as const;
const maintenancePhotoCategories = [
  ["existing_equipment", "Equipo y zona de actuación"],
  ["electrical_panel", "Cuadro eléctrico"],
  ["other", "Avería u otra evidencia"],
] as const;
const patternDimensions: Array<[string, string, Array<[string, string]>]> = [
  [
    "use",
    "Uso",
    [
      ["unknown", "No lo sé"],
      ["primary_residence", "Vivienda principal"],
      ["second_home", "Segunda residencia"],
      ["business", "Actividad empresarial"],
      ["other", "Otro"],
    ],
  ],
  [
    "days",
    "Días",
    [
      ["unknown", "No lo sé"],
      ["weekdays", "Laborables"],
      ["weekends", "Fines de semana"],
      ["all_days", "Todos"],
      ["custom", "Variable"],
    ],
  ],
  [
    "timeBand",
    "Franja",
    [
      ["unknown", "No lo sé"],
      ["solar", "Diurna/solar"],
      ["evening_night", "Tarde-noche"],
      ["distributed", "Distribuida"],
    ],
  ],
  [
    "activity",
    "Actividad",
    [
      ["unknown", "No lo sé"],
      ["continuous", "Continua"],
      ["shifts", "Turnos"],
      ["variable_peaks", "Variable/picos"],
      ["other", "Otra"],
    ],
  ],
];
function FvChoiceArt({ kind }: { kind: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 160 58"
      className="mb-3 h-14 w-full rounded-lg bg-sky-50"
    >
      {kind === "particular" ? (
        <>
          <path
            d="M24 44V25L56 8l32 17v19H24Z"
            fill="#bbf7d0"
            stroke="#047857"
            strokeWidth="3"
          />
          <path d="m43 27 10-6 10 6v10H43z" fill="#2563eb" />
          <path d="M105 14v25m-10-15h20" stroke="#eab308" strokeWidth="4" />
        </>
      ) : kind === "community" ? (
        <>
          <path
            d="M22 45V15h35v30M64 45V7h35v38M106 45V20h31v25"
            fill="#dbeafe"
            stroke="#1d4ed8"
            strokeWidth="3"
          />
          <path
            d="M31 24h8m8 0h3m-19 9h8m8 0h3m-2-16h8m-8 9h8m-8 9h8"
            stroke="#1d4ed8"
            strokeWidth="2"
          />
        </>
      ) : kind === "industry" ? (
        <>
          <path
            d="M20 45V29l27-15v14l25-14v31h-52Z"
            fill="#cbd5e1"
            stroke="#475569"
            strokeWidth="3"
          />
          <path d="M31 36h8m8 0h8m8 0h5" stroke="#f59e0b" strokeWidth="4" />
          <path d="M111 10v35m-12-23h24" stroke="#eab308" strokeWidth="4" />
        </>
      ) : kind === "orchard" ? (
        <>
          <circle cx="128" cy="15" r="9" fill="#facc15" />
          <path
            d="M18 44h124M27 38l18-11 18 11m-10 0 18-11 18 11m-10 0 18-11 18 11"
            fill="none"
            stroke="#15803d"
            strokeWidth="4"
          />
          <path
            d="m42 27 9-6 9 6m17 0 9-6 9 6m17 0 9-6 9 6"
            fill="#2563eb"
            stroke="#1d4ed8"
            strokeWidth="2"
          />
        </>
      ) : kind === "new" ? (
        <>
          <path
            d="M29 43h60L59 13 29 43Z"
            fill="#bbf7d0"
            stroke="#047857"
            strokeWidth="3"
          />
          <path d="M45 32h13v8H45z" fill="#2563eb" />
          <path d="M111 14v30m-12-15h24" stroke="#eab308" strokeWidth="4" />
        </>
      ) : kind === "expand" ? (
        <>
          <path
            d="M18 43h48L42 18 18 43Z"
            fill="#d1d5db"
            stroke="#64748b"
            strokeWidth="3"
          />
          <path
            d="M57 43 83 17l27 26Z"
            fill="#bbf7d0"
            stroke="#047857"
            strokeWidth="3"
          />
          <path d="m121 17 16 0m-8-8v16" stroke="#2563eb" strokeWidth="4" />
        </>
      ) : (
        <>
          <rect
            x="53"
            y="15"
            width="54"
            height="31"
            rx="5"
            fill="#dbeafe"
            stroke="#1d4ed8"
            strokeWidth="3"
          />
          <path d="M80 20 69 33h10l-2 8 14-16H80z" fill="#eab308" />
        </>
      )}
    </svg>
  );
}

function Art({ kind }: { kind: Service }) {
  return (
    <svg
      viewBox="0 0 200 110"
      className="h-28 w-full rounded-xl bg-gradient-to-br from-emerald-50 to-sky-100"
      role="img"
      aria-label={
        kind === "photovoltaic"
          ? "Ilustración fotovoltaica"
          : kind === "ev_charger"
            ? "Ilustración de cargador eléctrico"
            : "Ilustración de mantenimiento"
      }
    >
      <path d="M14 92h172" stroke="#64748b" strokeWidth="3" />
      {kind === "photovoltaic" ? (
        <>
          <path d="m38 65 62-40 62 40v25H38z" fill="#0f766e" />
          <path
            d="m53 63 47-30 47 30"
            stroke="white"
            strokeWidth="3"
            fill="none"
          />
          <circle cx="100" cy="28" r="12" fill="#fbbf24" />
        </>
      ) : kind === "ev_charger" ? (
        <>
          <path d="M53 43h94l-9-22H62z" fill="#2563eb" />
          <path d="M42 60h116v24H42z" rx="10" fill="#1d4ed8" />
          <circle cx="66" cy="83" r="10" fill="#334155" />
          <circle cx="134" cy="83" r="10" fill="#334155" />
          <path d="M100 49v24" stroke="white" strokeWidth="4" />
        </>
      ) : (
        <>
          <rect x="67" y="22" width="66" height="68" rx="8" fill="#7c3aed" />
          <path d="m100 34-19 29h17l-2 16 23-31h-17z" fill="#fff" />
        </>
      )}
    </svg>
  );
}
export default function CommercialWizard() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const utils = trpc.useUtils();
  const initialQuery = useRef(new URLSearchParams(window.location.search));
  const requestedId =
    Number(initialQuery.current.get("requestId")) || undefined;
  const draftKey = initialQuery.current.get("draftKey") || crypto.randomUUID();
  const [draft, setDraftRaw] = useState<Draft>(emptyDraft);
  const draftRef = useRef(draft);
  const draftRevision = useRef(0);
  draftRef.current = draft;
  const [step, setStep] = useState(0);
  const [manualEntryRequested, setManualEntryRequested] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [partyTypeError, setPartyTypeError] = useState("");
  const [sourceError, setSourceError] = useState("");
  const [contactValidationAttempted, setContactValidationAttempted] = useState(false);
  const [contactGateMessage, setContactGateMessage] = useState("");
  const manualSupplyRef = useRef<HTMLDetailsElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const partyTypeInputRef = useRef<HTMLSelectElement>(null);
  const sourceRadioRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const activeStages = commercialWizardStages(draft.serviceType);
  const activeStage = activeStages[step]?.id ?? "service";
  const photoCategories = draft.serviceType === "ev_charger"
    ? evPhotoCategories
    : draft.serviceType === "maintenance"
      ? maintenancePhotoCategories
      : photovoltaicPhotoCategories;
  const [id, setId] = useState<number | undefined>(requestedId);
  const idRef = useRef(id);
  idRef.current = id;
  const [version, setVersion] = useState<number>();
  const versionRef = useRef(version);
  versionRef.current = version;
  const [key] = useState(() => draftKey);
  const hydrated = useRef(false);
  const createdInSession = useRef(false);
  const sourceSelectionRequired = !id || createdInSession.current || Number(draft.need.sourceGateVersion) === 1;
  const submitInFlight = useRef(false);
  const confirmLocationInFlight = useRef(false);
  const roofSaveRef = useRef<((versionOverride?: number) => Promise<boolean>) | null>(null);
  const [commercialGoogleMap, setCommercialGoogleMap] = useState<google.maps.Map | null>(null);
  const geocodeRequest = useRef(0);
  const autoResolvedAddresses = useRef(new Set<string>());
  const [candidateEdits, setCandidateEdits] = useState<Record<string, string>>(
    {}
  );
  const [candidateAlternativeSelections, setCandidateAlternativeSelections] = useState<Record<string, number>>({});
  const [billSupplyAssignments, setBillSupplyAssignments] = useState<Record<string, string>>({});
  const [selectedBillDocumentId, setSelectedBillDocumentId] = useState<number>();
  const [activeSupplyIndex, setActiveSupplyIndex] = useState(0);
  const setDraft = (update: React.SetStateAction<Draft>) => {
    const current = draftRef.current;
    const next = typeof update === "function" ? (update as (value: Draft) => Draft)(current) : update;
    draftRef.current = next;
    draftRevision.current += 1;
    setDraftRaw(next);
  };
  const setDraftSnapshot = (next: Draft) => {
    draftRef.current = next;
    draftRevision.current += 1;
    setDraftRaw(next);
  };
  const setDraftFromServer = (next: Draft) => {
    draftRef.current = next;
    setDraftRaw(next);
  };
  useEffect(() => {
    setConfirmPendingSend(false);
  }, [step]);
  const [manualPeriodStart, setManualPeriodStart] = useState("");
  const [manualPeriodEnd, setManualPeriodEnd] = useState("");
  const [saving, setSaving] = useState(false);
  const persistQueue = useRef<Promise<any>>(Promise.resolve(null));
  const [submitting, setSubmitting] = useState(false);
  const [confirmingLocation, setConfirmingLocation] = useState(false);
  const [pin, setPin] = useState<Pin | null>(null);
  const [roofMapActive, setRoofMapActive] = useState(false);
  const roofMapActiveRef = useRef(false);
  function setRoofViewport(active: boolean) { roofMapActiveRef.current = active; setRoofMapActive(active); }
  const [locationSource, setLocationSource] = useState<"google_geocoder" | "cartociudad" | "google_maps" | "manual">("manual");
  const [mapError, setMapError] = useState("");
  const [addressSearchPending, setAddressSearchPending] = useState(false);
  const [debouncedAddress, setDebouncedAddress] = useState("");
  const [selectedSuggestion, setSelectedSuggestion] = useState("");
  const [confirmPendingSend, setConfirmPendingSend] = useState(false);
  const [choices, setChoices] = useState<any[]>([]);
  const [resolvedCartoCandidate, setResolvedCartoCandidate] = useState<ResolvedCartoCiudadCandidate | null>(null);
  const [saveState, setSaveState] = useState("");
  const [customerContextNotice, setCustomerContextNotice] = useState("");
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const [siteDraft, setSiteDraft] = useState({ name: "", addressLine1: "", postalCode: "", city: "" });
  const [contactDraft, setContactDraft] = useState({ name: "", role: "", phone: "", email: "", contactRole: "other" as "billing" | "technical" | "sales" | "legal" | "other", primary: false });
  const [siteCreationKey, setSiteCreationKey] = useState(() => crypto.randomUUID());
  const [contactClientKey, setContactClientKey] = useState(() => crypto.randomUUID());
  const evCatalog = trpc.evSales.listCatalog.useQuery(undefined, { enabled: draft.serviceType === "ev_charger", staleTime: 60_000 });
  useEffect(() => {
    if (draft.serviceType !== "ev_charger" || evCatalog.isLoading || evCatalog.isError) return;
    setDraft(current => {
      const reconciled = reconcileEvCatalogOption(current.need, evCatalog.data ?? []);
      if (reconciled.evOptionId === current.need.evOptionId) return current;
      return { ...current, need: reconciled };
    });
  }, [draft.serviceType, draft.need.siteKind, draft.need.phase, draft.need.evOptionId, evCatalog.data, evCatalog.isLoading, evCatalog.isError]);
  const save = trpc.commercialIntake.saveDraft.useMutation();
  const savePhotoCategory = trpc.commercialIntake.savePhotoCategory.useMutation();
  const applyBillBatch = trpc.commercialIntake.applyBillBatch.useMutation();
  const reviewBillField = trpc.commercialIntake.reviewBillField.useMutation();
  const billBatchKeys = useRef(new Map<string, { key: string; requestId: number; expectedVersion: number; entries: Array<{ documentId: number; field: string; value: string; supplyId: string }>; baselineParties: Record<string, any>; baselineLocation: Record<string, any>; result?: any }>());
  const retryBillExtraction = trpc.commercialIntake.retryBillExtraction.useMutation();
  const geocode = trpc.commercialIntake.geocode.useMutation();
  const autocomplete = trpc.commercialIntake.placeAutocomplete.useQuery(
    { input: debouncedAddress },
    {
      enabled: debouncedAddress.trim().length >= 3,
      staleTime: 0,
      trpc: { abortOnUnmount: true },
    },
  );
  const currentAddress = String(draft.location.address ?? "").trim();
  useEffect(() => {
    const value = currentAddress;
    if (value.length < 3 || value === selectedSuggestion) {
      setDebouncedAddress("");
      return;
    }
    const timer = window.setTimeout(() => setDebouncedAddress(value), 300);
    return () => window.clearTimeout(timer);
  }, [currentAddress, selectedSuggestion]);
  const confirmLocation = trpc.commercialIntake.confirmLocation.useMutation();
  const createCustomerSite = trpc.commercialIntake.createCustomerSite.useMutation();
  const createCustomerContact = trpc.commercialIntake.createCustomerContact.useMutation();
  const send = trpc.commercialIntake.submit.useMutation();
  const context = trpc.commercialIntake.contextSearch.useQuery(
    {
      query: "__",
      customerPartnerId: draft.customerPartnerId ?? undefined,
      requestId: id,
    },
    { enabled: Boolean(user && draft.customerPartnerId) }
  );
  const customerSearch = trpc.commercialIntake.contextSearch.useQuery(
    { query: query.trim().length >= 2 ? query.trim() : "__" },
    { enabled: Boolean(user && query.trim().length >= 2) }
  );
  const customerOptions = [
    ...(customerSearch.data?.customers ?? []),
    ...(context.data?.customers ?? []),
  ].filter((customer: any, index: number, all: any[]) =>
    all.findIndex((candidate: any) => Number(candidate.id) === Number(customer.id)) === index
  );
  const docs = trpc.commercialIntake.documents.useQuery(
    { id: id ?? 0 },
    { enabled: Boolean(id), refetchInterval: billProgressRefetchInterval }
  );
  const needsKnownInstallation =
    draft.serviceType === "photovoltaic" &&
    ["expand", "batteries"].includes(String(draft.need.objective));
  const knownInstallations = trpc.commercialIntake.knownInstallations.useQuery(
    { customerPartnerId: draft.customerPartnerId ?? 0 },
    { enabled: Boolean(draft.customerPartnerId && needsKnownInstallation) }
  );
  const request = trpc.commercialIntake.get.useQuery(
    { id: id ?? 0 },
    { enabled: Boolean(id) }
  );
  const locationHistory = trpc.commercialIntake.history.useQuery(
    { id: id ?? 0 },
    { enabled: Boolean(id) }
  );
  const primarySupply = (draft.parties.supplies?.[activeSupplyIndex] ??
    {}) as Record<string, any>;
  const supplyLocationSeed = commercialSupplyLocationSeed(draft.parties);
  const postalLocationProposal = commercialPostalLocationProposal(draft.location, draft.parties);
  const [editAddressDetails, setEditAddressDetails] = useState(false);
  const [postalAsInstallation, setPostalAsInstallation] = useState<boolean | null>(null);
  const invoiceDocuments = (docs.data ?? []).filter((doc: any) => doc.documentKind === "electricity_bill" && doc.extraction?.status !== "processing" && doc.extraction?.fields);
  useEffect(() => {
    if (!invoiceDocuments.length || (draftRef.current.parties.supplies ?? []).length) return;
    setDraft(current => current.parties.supplies?.length
      ? current
      : { ...current, parties: { ...current.parties, supplies: [{ id: crypto.randomUUID() }] } });
  }, [invoiceDocuments.length]);
  function billDocumentSupplyId(doc: any) {
    const groupSupplyId = doc.captureGroupId
      ? invoiceDocuments.find((sibling: any) => sibling.captureGroupId === doc.captureGroupId && (sibling.extraction?.supplyId || billSupplyAssignments[String(sibling.id)]))
      : undefined;
    return String(doc.extraction?.supplyId ?? billSupplyAssignments[String(doc.id)] ?? groupSupplyId?.extraction?.supplyId ?? billSupplyAssignments[String(groupSupplyId?.id)] ?? ((draft.parties.supplies?.length ?? 0) === 1 ? draft.parties.supplies[0]?.id : "") ?? "");
  }
  const selectedBillDocument = invoiceDocuments.find((doc: any) => Number(doc.id) === selectedBillDocumentId && billDocumentSupplyId(doc) === String(primarySupply.id ?? ""))
    ?? invoiceDocuments.find((doc: any) => billDocumentSupplyId(doc) === String(primarySupply.id ?? ""))
    ?? ((draft.parties.supplies?.length ?? 0) <= 1 ? invoiceDocuments[0] : undefined);
  function billCandidate(field: string) {
    const candidate = selectedBillDocument?.extraction?.fields?.[field];
    if (!candidate) return undefined;
    const key = `${selectedBillDocument.id}:${field}`;
    return { candidate, key, value: commercialBillCandidateValue(candidate, candidateEdits[key]) };
  }
  function currentFinalSupplyValue(field: string, legacyField?: string) {
    const value = primarySupply[field] ?? (activeSupplyIndex === 0 && legacyField ? draft.parties[legacyField] : undefined);
    return value == null ? "" : String(value);
  }
  function finalSupplyFieldValue(field: string, legacyField?: string, candidateField = field) {
    const currentValue = currentFinalSupplyValue(field, legacyField);
    const candidate = billCandidate(candidateField);
    if (currentValue.trim()) return currentValue;
    if (!candidate) return currentValue;
    if (field === "tariff" && !["2.0TD", "3.0TD", "6.1TD", "6.2TD", "6.3TD", "6.4TD", "other"].includes(candidate.value)) return "unknown";
    return candidate.value;
  }
  function updateFinalSupplyField(field: string, legacyField: string | undefined, value: string, candidateField = field) {
    const currentValue = currentFinalSupplyValue(field, legacyField);
    const proposal = billCandidate(candidateField);
    const hasPendingBillBatch = billBatchKeys.current.size > 0;
    if (!currentValue.trim() && proposal && !hasPendingBillBatch) {
      setCandidateEdits(current => ({ ...current, [proposal.key]: value }));
      return;
    }
    updatePrimarySupply({ [field]: value });
    if (activeSupplyIndex === 0 && legacyField)
      setDraft(current => ({ ...current, parties: { ...current.parties, [legacyField]: value } }));
  }
  function draftWithExplicitBillEdits(sourceDraft: Draft): Draft {
    if (!Object.keys(candidateEdits).length || !invoiceDocuments.length) return sourceDraft;
    let parties = sourceDraft.parties;
    let location = sourceDraft.location;
    let supplies = [...(parties.supplies ?? [])];
    for (const doc of invoiceDocuments) {
      const supplyId = billDocumentSupplyId(doc);
      const target = supplies.findIndex((supply: any) => String(supply.id ?? "") === supplyId);
      for (const field of Object.keys(doc.extraction?.fields ?? {})) {
        const key = `${doc.id}:${field}`;
        if (!Object.prototype.hasOwnProperty.call(candidateEdits, key)) continue;
        const value = candidateEdits[key];
        if (field === "postalAddress") {
          location = { ...location, billAddress: value };
        } else if (field === "supplyAddress" && target >= 0) {
          supplies[target] = { ...supplies[target], address: value };
        }
      }
    }
    if (supplies !== parties.supplies) parties = { ...parties, supplies };
    return parties === sourceDraft.parties && location === sourceDraft.location
      ? sourceDraft
      : { ...sourceDraft, parties, location };
  }
  function billSourceNote(field: string) {
    const proposal = billCandidate(field);
    const currentField = field === "supplyAddress" ? "address" : field;
    const occupied = field === "postalAddress" ? String(draft.location.billAddress ?? "").trim() : currentFinalSupplyValue(currentField, field === "supplyAddress" ? "supplyAddress" : undefined).trim();
    if (!proposal || occupied) return null;
    const candidate = proposal.candidate;
    const locallyCorrected = candidateEdits[proposal.key] != null && candidateEdits[proposal.key].trim() !== (Array.isArray(candidate.value) ? candidate.value.join("\n") : String(candidate.value ?? "")).trim();
    const source = candidate.sourceProvenance ?? candidate.provenance ?? "factura";
    const sourceLabel = source === "not_found_in_document" ? "No encontrado en la factura" : source;
    return <span className="text-xs text-muted-foreground">{candidate.value == null ? sourceLabel : `Propuesta OCR · ${sourceLabel}`}{candidate.sourceDocumentPage ?? candidate.page ? ` · pág. ${candidate.sourceDocumentPage ?? candidate.page}` : ""}{candidate.reviewReason === "multiple_candidates" ? " · alternativas ambiguas: confirma una" : locallyCorrected ? " · corrección pendiente de aplicar" : candidate.correctedByHuman ? " · corregido manualmente" : candidate.value == null ? "" : " · revisar"}</span>;
  }
  function finalReadingValue(field: "contractedPowerPeriods" | "consumptionPeriods", period: string) {
    const supplyKey = field === "contractedPowerPeriods" ? "contractedPower" : "consumptionPeriods";
    const current = (primarySupply[supplyKey] ?? []).find((reading: any) => reading.period === period && reading.source?.entryKind === "current_manual")
      ?? (primarySupply[supplyKey] ?? []).find((reading: any) => reading.period === period);
    if (current) return current.value;
    return billCandidate(field)?.candidate.normalized?.find((reading: any) => reading.period === period)?.value ?? "";
  }
  function updateFinalReading(field: "contractedPowerPeriods" | "consumptionPeriods", period: string, value: string) {
    const supplyKey = field === "contractedPowerPeriods" ? "contractedPower" : "consumptionPeriods";
    const hasCurrent = (primarySupply[supplyKey] ?? []).some((reading: any) => reading.period === period);
    const proposal = billCandidate(field);
    const normalized = proposal?.candidate.normalized;
    if (!hasCurrent && proposal && Array.isArray(normalized) && normalized.length) {
      let changed = false;
      const lines = normalized.map((reading: any) => {
        const nextValue = reading.period === period && !changed ? (changed = true, value) : String(reading.value);
        const interval = reading.startDate || reading.endDate ? ` ${reading.startDate ?? ""} - ${reading.endDate ?? ""}` : "";
        return `${reading.period}: ${nextValue} ${reading.unit ?? (field === "contractedPowerPeriods" ? "kW" : "kWh")}${interval}`;
      });
      setCandidateEdits(current => ({ ...current, [proposal.key]: lines.join("\n") }));
      return;
    }
    updateReading(field === "contractedPowerPeriods" ? "contractedPower" : "consumptionPeriods", period, value);
  }
  const replay = trpc.commercialIntake.getDraftByKey.useQuery(
    { idempotencyKey: key },
    {
      enabled: Boolean(user && !id && initialQuery.current.has("draftKey")),
      retry: false,
    }
  );
  const requestStatus = (request.data ?? replay.data)?.status;
  const isPreviouslySubmittedResponse = requestStatus === "waiting_customer";
  const readOnlyOfficeReview = Boolean(id) && !["draft", "waiting_customer"].includes(String(requestStatus ?? ""));
  function updatePrimarySupply(patch: Record<string, any>) {
    setDraft(d => {
      const supplies = [...(d.parties.supplies ?? [])];
      supplies[activeSupplyIndex] = {
        ...(supplies[activeSupplyIndex] ?? {}),
        ...patch,
      };
      return { ...d, parties: { ...d.parties, supplies } };
    });
  }
  function updateReading(
    kind: "contractedPower" | "consumptionPeriods",
    period: string,
    value: string
  ) {
    const unit = kind === "contractedPower" ? "kW" : "kWh";
    const existing = [...(primarySupply[kind] ?? [])];
    const manualIndex = existing.findIndex(
      (r: any) =>
        r.period === period && r.source?.entryKind === "current_manual"
    );
    if (manualIndex >= 0) existing.splice(manualIndex, 1);
    const parsed = Number(value.replace(",", "."));
    if (value.trim() && Number.isFinite(parsed) && parsed >= 0)
      existing.push({
        period,
        value: parsed,
        unit,
        ...(kind === "consumptionPeriods" && manualPeriodStart
          ? { startDate: manualPeriodStart }
          : {}),
        ...(kind === "consumptionPeriods" && manualPeriodEnd
          ? { endDate: manualPeriodEnd }
          : {}),
        tariffAtCapture: primarySupply.tariff ?? "unknown",
        source: { provenance: "manual", entryKind: "current_manual" },
      });
    updatePrimarySupply({ [kind]: existing });
  }
  async function applyBillProposalsAndPersist(targetStep: number) {
    let current = draftRef.current;
    const locationStage = activeStages[targetStep]?.id === "location";
    const locationSeed = commercialSupplyLocationSeed(current.parties);
    if (locationStage && !String(current.location.address ?? "").trim() && locationSeed.address) {
      current = { ...current, location: { ...current.location, address: locationSeed.address } };
      setDraftSnapshot(current);
    }
    const currentSupplies = current.parties.supplies ?? [];
    const supplies = invoiceDocuments.length
      ? (currentSupplies.length ? currentSupplies : [{}]).map((supply: any) => ({ ...supply, id: supply.id ?? crypto.randomUUID() }))
      : currentSupplies;
    const snapshot: Draft = { ...current, parties: { ...current.parties, supplies } };
    setDraftSnapshot(snapshot);
    const proposal = buildCommercialBillProposalBatch({
      documents: invoiceDocuments,
      supplies,
      activeSupplyIndex,
      assignments: billSupplyAssignments,
      edits: candidateEdits,
      alternativeSelections: candidateAlternativeSelections,
      parties: snapshot.parties,
      location: snapshot.location,
    });
    if (proposal.unresolvedDocumentIds.length) {
      setError("Asocia cada factura con su suministro antes de continuar; no se aplicarán propuestas a un suministro supuesto.");
      setSaveState("Hay facturas pendientes de asociar.");
      return null;
    }
    const requestedId = id;
    let identity = requestedId ? JSON.stringify({ requestId: Number(requestedId), entries: proposal.entries }) : "";
    let operation = identity ? billBatchKeys.current.get(identity) : undefined;
    // A pending batch may already be reflected in the draft after readback,
    // making the newly derived proposal empty. Recover the unfinished operation
    // by request id and replay its original payload/key before proceeding.
    if (!operation && requestedId) {
      operation = Array.from(billBatchKeys.current.values()).find(candidate => candidate.requestId === Number(requestedId));
      if (operation) identity = JSON.stringify({ requestId: operation.requestId, entries: operation.entries });
    }
    let saved: any;
    try {
      if (proposal.entries.length && !operation) {
        // Save only final fields explicitly entered by the user. OCR proposals stay
        // local until the audited batch accepts them, so OCC never sees them as occupied.
        saved = await persist(step, snapshot, versionRef.current, requestedId);
        if (!saved) return null;
        identity = JSON.stringify({ requestId: Number(saved.id), entries: proposal.entries });
        operation = billBatchKeys.current.get(identity);
        if (!operation) {
          operation = {
            key: crypto.randomUUID(), requestId: Number(saved.id), expectedVersion: Number(saved.version), entries: proposal.entries,
            baselineParties: structuredClone(snapshot.parties), baselineLocation: structuredClone(snapshot.location),
          };
          billBatchKeys.current.set(identity, operation);
        }
      }
      if (!proposal.entries.length && !operation) {
        saved = await persist(targetStep, snapshot, versionRef.current, requestedId);
        if (!saved) return null;
        if (proposal.skippedOccupied.length) setSaveState("Los campos confirmados se conservaron; la solicitud continúa.");
        else if (proposal.skippedInformational.length) setSaveState("Las cifras sin periodos explícitos quedan como información de factura; no se convierten en lecturas.");
        return saved;
      }
      if (!operation) throw new Error("BILL_BATCH_OPERATION_UNAVAILABLE");
      // Keep the idempotent operation until both authenticated readback and the
      // final draft save complete. If readback fails after server commit, retry
      // must reconcile the same operation, never mint a new key at stale OCC.
      const response: any = await applyBillBatch.mutateAsync({
        requestId: operation.requestId,
        expectedVersion: operation.expectedVersion,
        idempotencyKey: operation.key,
        entries: operation.entries,
      });
      const result: any = response?.replayed && operation.result?.results
        ? { ...response, results: operation.result.results }
        : response;
      operation.result = result;
      const [, refreshedRequest] = await Promise.all([docs.refetch(), request.refetch()]);
      const fresh = refreshedRequest?.data;
      const freshVersion = Number(fresh?.version ?? operation.expectedVersion);
      const latest = draftRef.current;
      let parties = latest.parties;
      let location = latest.location;
      if (fresh?.parties || fresh?.location) {
        for (const entry of operation.entries) {
          parties = mergeAppliedBillReadback({ currentParties: parties, baselineParties: operation.baselineParties, serverParties: fresh.parties ?? {}, field: entry.field, supplyId: entry.supplyId });
          location = mergeAppliedBillLocationReadback({ currentLocation: location, baselineLocation: operation.baselineLocation, serverLocation: fresh.location ?? {}, field: entry.field });
        }
      }
      // OCR can populate a unique supply address only in the canonical readback.
      // Propose it for installation after merge/readback, without copying billing
      // data or guessing when multiple supplies point to different locations.
      if (locationStage && !String(location.address ?? "").trim()) {
        const readbackLocationSeed = commercialSupplyLocationSeed(parties);
        if (readbackLocationSeed.address) {
          location = { ...location, address: readbackLocationSeed.address, installationAddress: readbackLocationSeed.address };
        }
      }
      const appliedDraft: Draft = { ...latest, parties, location };
      setDraftSnapshot(appliedDraft);
      setVersion(freshVersion);
      versionRef.current = freshVersion;
      setId(operation.requestId);
      setDraftUrl(operation.requestId);
      let finalRow: any;
      try {
        finalRow = await persist(targetStep, appliedDraft, freshVersion, operation.requestId);
      } catch (persistError) {
        // saveDraft may commit while its response/read-side invalidation fails.
        // Reconcile the persisted step before retrying with a newer OCC version.
        const reconciled = (await request.refetch())?.data;
        const persistedTarget = persistedCommercialWizardStep(appliedDraft.serviceType, targetStep);
        if (Number(reconciled?.version) > freshVersion && Number(reconciled?.need?.wizardStep) === persistedTarget) {
          finalRow = reconciled;
          setVersion(Number(reconciled.version));
          versionRef.current = Number(reconciled.version);
        } else {
          throw persistError;
        }
      }
      if (!finalRow) return null;
      billBatchKeys.current.delete(identity);
      const skippedCount = proposal.skippedOccupied.length + proposal.skippedInformational.length;
      const periodOverlap = result.results?.some((review: any) => review.periodOverlapDetected);
      const periodMetadata = result.results?.some((review: any) => review.periodMetadataReviewRequired);
      setSaveState(periodOverlap
        ? `Factura revisada y guardada · ${result.appliedCount} datos aplicados; hay lecturas solapadas que deben revisarse antes de comparar.${periodMetadata ? " También faltan intervalos explícitos para algunas lecturas." : ""}${skippedCount ? ` ${skippedCount} datos confirmados o informativos se conservaron.` : ""}`
        : periodMetadata
          ? `Factura revisada y guardada · ${result.appliedCount} datos aplicados; falta asociar con certeza algunos intervalos. No se supusieron fechas.${skippedCount ? ` ${skippedCount} datos confirmados o informativos se conservaron.` : ""}`
          : skippedCount
            ? `Factura revisada y guardada · ${result.appliedCount} datos aplicados; ${skippedCount} datos confirmados o informativos se conservaron.`
            : `Factura revisada y guardada · ${result.appliedCount} datos aplicados.`);
      return finalRow;
    } catch (error: any) {
      setError(commercialBillReviewErrorMessage(error, "batch"));
      setSaveState("No se pudo confirmar el lote. Reintentar conserva la misma clave y los mismos datos; no se avanzó de paso.");
      return null;
    }
  }

  async function createCanonicalSite() {
    if (!draft.customerPartnerId || !siteDraft.name.trim() || !siteDraft.addressLine1.trim()) return;
    try {
      const saved: any = id ? { id, version } : await persist();
      if (!saved) return;
      const result: any = await createCustomerSite.mutateAsync({
        requestId: Number(saved.id),
        version: Number(saved.version),
        idempotencyKey: siteCreationKey,
        name: siteDraft.name.trim(),
        addressLine1: siteDraft.addressLine1.trim(),
        postalCode: siteDraft.postalCode.trim() || null,
        city: siteDraft.city.trim() || null,
        countryCode: "ES",
        role: "customer",
      });
      setId(Number(saved.id));
      setVersion(Number(result.version));
      setDraft(current => ({ ...current, siteId: Number(result.siteId) }));
      if (!result.idempotentReplay) setSiteCreationKey(crypto.randomUUID());
      setSiteDraft({ name: "", addressLine1: "", postalCode: "", city: "" });
      await utils.commercialIntake.contextSearch.invalidate();
      await utils.commercialIntake.get.invalidate({ id: Number(saved.id) });
      setSaveState("Sede canónica creada y vinculada");
    } catch (e: any) {
      setError(e?.message ?? "No se pudo crear la sede.");
    }
  }
  async function createCanonicalContact() {
    if (!draft.customerPartnerId || !contactDraft.name.trim()) return;
    try {
      const saved: any = id ? { id, version } : await persist();
      if (!saved) return;
      const result: any = await createCustomerContact.mutateAsync({
        requestId: Number(saved.id),
        version: Number(saved.version),
        clientKey: contactClientKey,
        name: contactDraft.name.trim(),
        role: contactDraft.role.trim() || null,
        contactRole: contactDraft.contactRole,
        phone: contactDraft.phone.trim() || null,
        email: contactDraft.email.trim() || null,
        primary: contactDraft.primary,
      });
      setId(Number(saved.id));
      setVersion(Number(result.version));
      setDraft(current => ({
        ...current,
        contactId: contactDraft.primary ? Number(result.contactId) : current.contactId,
        parties: {
          ...current.parties,
          contacts: result.contacts,
          ...(contactDraft.primary ? {
            contactName: contactDraft.name.trim(),
            ...(contactDraft.phone.trim() ? { phone: contactDraft.phone.trim(), contactMethod: contactDraft.phone.trim() } : {}),
            ...(contactDraft.email.trim() ? { email: contactDraft.email.trim(), contactMethod: contactDraft.email.trim() } : {}),
          } : {}),
        },
      }));
      if (!result.idempotentReplay) setContactClientKey(crypto.randomUUID());
      setContactDraft({ name: "", role: "", phone: "", email: "", contactRole: "other", primary: false });
      setSaveState("Contacto canónico creado y vinculado");
    } catch (e: any) {
      setError(e?.message ?? "No se pudo crear el contacto.");
    }
  }
  function addExistingCanonicalContact(contact: any, primary = false) {
    const contactId = Number(contact.id);
    if (!Number.isSafeInteger(contactId)) return;
    const existing = (draft.parties.contacts ?? []).some((item: any) => Number(item.contactId) === contactId);
    if (existing && !primary) return;
    if (existing && primary) {
      setDraft(current => ({ ...current, contactId, parties: { ...current.parties, contactName: String(contact.displayName), ...(contact.primaryPhone ? { phone: String(contact.primaryPhone), contactMethod: String(contact.primaryPhone) } : {}), ...(contact.primaryEmail ? { email: String(contact.primaryEmail), contactMethod: String(contact.primaryEmail) } : {}), contacts: (current.parties.contacts ?? []).map((item: any) => ({ ...item, primary: Number(item.contactId) === contactId })) } }));
      return;
    }
    const next = [...(draft.parties.contacts ?? []), {
      name: String(contact.displayName),
      role: String(contact.jobTitle ?? contact.role ?? ""),
      phone: String(contact.primaryPhone ?? ""),
      email: String(contact.primaryEmail ?? ""),
      primary,
      contactId,
      siteId: contact.siteId == null ? null : Number(contact.siteId),
    }];
    setDraft(current => ({ ...current, contactId: primary ? contactId : current.contactId, parties: { ...current.parties, ...(primary ? { contactName: String(contact.displayName), ...(contact.primaryPhone ? { phone: String(contact.primaryPhone), contactMethod: String(contact.primaryPhone) } : {}), ...(contact.primaryEmail ? { email: String(contact.primaryEmail), contactMethod: String(contact.primaryEmail) } : {}) } : {}), contacts: next.map(item => primary ? { ...item, primary: Number(item.contactId) === contactId } : item) } }));
  }
  function changeCanonicalCustomer(customer: any) {
    const nextCustomerId = Number(customer.id);
    if (draft.customerPartnerId === nextCustomerId) return;
    const hasInstallationLink = Boolean(draft.need.knownInstallationId);
    const hasContactData = Boolean(
      draft.contactId || draft.parties.contactName || draft.parties.phone ||
      draft.parties.email || draft.parties.contactMethod ||
      draft.parties.extraContactName || draft.parties.extraContactMethod ||
      (draft.parties.contacts ?? []).length
    );
    if ((hasContactData || hasInstallationLink) && !window.confirm(
      `Al cambiar de cliente se retirarán del borrador los contactos y canales del cliente actual${hasInstallationLink ? " y se desvinculará la instalación conocida seleccionada" : ""} para evitar asociaciones incorrectas. Se conservarán la descripción, potencia, dirección, datos de suministro, OCR y el resto de cambios para revisión. ¿Continuar?`
    )) return;
    setDraft(current => ({
      ...current,
      customerPartnerId: nextCustomerId,
      contactId: null,
      siteId: null,
      need: {
        ...current.need,
        knownInstallationId: undefined,
        knownInstallationLabel: undefined,
      },
      parties: {
        ...current.parties,
        clientName: String(customer.tradeName || customer.legalName),
        contactName: "",
        phone: "",
        email: "",
        contactMethod: "",
        extraContactName: "",
        extraContactMethod: "",
        contacts: [],
      },
    }));
    setCustomerContextNotice(`Cliente cambiado: se retiraron los contactos del cliente anterior${hasInstallationLink ? " y se desvinculó su instalación conocida" : ""}. Revisa la descripción, potencia y dirección conservadas antes de guardar.`);
  }
  function removeCanonicalContact(contact: any) {
    const contactId = Number(contact.contactId);
    const wasPrimary = Boolean(contact.primary) || (Number.isSafeInteger(contactId) && Number(draft.contactId) === contactId);
    setDraft(current => {
      const nextContacts = (current.parties.contacts ?? []).filter((item: any) =>
        contact.contactId != null ? Number(item.contactId) !== contactId : item.clientKey !== contact.clientKey
      ).map((item: any) => ({ ...item, primary: wasPrimary ? false : Boolean(item.primary) }));
      const nextParties: any = { ...current.parties, contacts: nextContacts };
      if (wasPrimary) {
        if (String(nextParties.contactName ?? "") === String(contact.name ?? "")) nextParties.contactName = "";
        if (String(nextParties.phone ?? "") === String(contact.phone ?? "")) nextParties.phone = "";
        if (String(nextParties.email ?? "") === String(contact.email ?? "")) nextParties.email = "";
        if ([contact.phone, contact.email].filter(Boolean).includes(nextParties.contactMethod)) nextParties.contactMethod = "";
      }
      return { ...current, ...(wasPrimary ? { contactId: null } : {}), parties: nextParties };
    });
    setSaveState(wasPrimary
      ? "Contacto principal retirado. Revisa el nombre y los canales antes de guardar."
      : "Contacto retirado del borrador.");
  }
  function setDraftUrl(requestId: number) {
    const url = new URL(window.location.href);
    url.searchParams.delete("draftKey");
    url.searchParams.set("requestId", String(requestId));
    window.history.replaceState({}, "", url);
  }
  function setDraftKeyUrl() {
    const url = new URL(window.location.href);
    url.searchParams.set("draftKey", key);
    window.history.replaceState({}, "", url);
  }
  useEffect(() => {
    if (!user || hydrated.current) return;
    const row: any = request.data ?? replay.data;
    if (!row) return;
    hydrated.current = true;
    const restoredParties = row.parties ?? {};
    const existingLocation = row.location ?? {};
    const supplyAddress = commercialSupplyLocationSeed(restoredParties).address;
    const restored: Draft = {
      serviceType: row.serviceType ?? null,
      source: row.source ?? "pending",
      customerPartnerId: row.customerPartnerId == null ? null : Number(row.customerPartnerId),
      contactId: row.contactId == null ? null : Number(row.contactId),
      siteId: row.siteId == null ? null : Number(row.siteId),
      need: row.need ?? {},
      parties: restoredParties,
      // Seed from the supply-site address only, never the postal address.
      location: !String(existingLocation.address ?? "").trim() && supplyAddress
        ? { ...existingLocation, address: supplyAddress }
        : existingLocation,
    };
    if (row.geoConfirmedAt && row.latitude != null && row.longitude != null)
      setPin({ latitude: Number(row.latitude), longitude: Number(row.longitude) });
    setDraft({
      ...restored,
      parties: {
        ...restored.parties,
        supplies: (restored.parties.supplies ?? []).map((s: any) => ({
          ...s,
          id: s.id ?? crypto.randomUUID(),
        })),
      },
    });
    setId(Number(row.id));
    setVersion(Number(row.version));
    const restoredStep = ["draft", "waiting_customer"].includes(String(row.status))
      ? restoreCommercialWizardStep(restored.serviceType, restored.need.wizardStep)
      : commercialWizardStages(restored.serviceType).findIndex(stage => stage.id === "summary");
    setStep(Math.max(0, restoredStep));
    setDraftUrl(Number(row.id));
  }, [user, request.data, replay.data]);
  useEffect(() => {
    if (replay.data && !id) {
      setId(Number((replay.data as any).id));
    }
  }, [replay.data, id]);
  function normalizeEmailForDraft(): string {
    const email = String(draftRef.current.parties.email ?? "").trim();
    if (email !== String(draftRef.current.parties.email ?? "")) {
      setDraft(current => ({ ...current, parties: { ...current.parties, email } }));
    }
    return email;
  }
  function validateRequiredContacts(): boolean {
    if (isPreviouslySubmittedResponse) {
      if (hasCommercialSubmissionContactMethod(draftRef.current.parties)) {
        setPhoneError("");
        setEmailError("");
        setError("");
        setContactGateMessage("");
        return true;
      }
      setContactValidationAttempted(true);
      setContactGateMessage("Añade un teléfono o email válido para reenviar esta solicitud ya enviada.");
      setTimeout(() => {
        if (!isValidCommercialPhone(draftRef.current.parties.phone)) phoneInputRef.current?.focus();
        else emailInputRef.current?.focus();
      }, 0);
      return false;
    }
    setContactValidationAttempted(true);
    const phone = String(draftRef.current.parties.phone ?? "").trim();
    const email = String(draftRef.current.parties.email ?? "").trim();
    const nextPhoneError = isValidCommercialPhone(phone) ? "" : phone ? "Introduce un teléfono válido." : "El teléfono es obligatorio.";
    const nextEmailError = isValidCommercialEmail(email) ? "" : email ? "Introduce un email válido." : "El email es obligatorio.";
    setPhoneError(nextPhoneError);
    setEmailError(nextEmailError);
    if (nextPhoneError || nextEmailError) {
      setError("");
      setContactGateMessage("Completa ambos datos de contacto obligatorios antes de continuar.");
      const detailsStep = activeStages.findIndex(stage => stage.id === "details");
      if (detailsStep >= 0 && step !== detailsStep) setStep(detailsStep);
      window.setTimeout(() => {
        if (nextPhoneError) phoneInputRef.current?.focus();
        else emailInputRef.current?.focus();
      }, 0);
      return false;
    }
    setError("");
    setContactGateMessage("");
    if (phone !== String(draftRef.current.parties.phone ?? "") || email !== String(draftRef.current.parties.email ?? "")) {
      setDraft(current => ({ ...current, parties: { ...current.parties, phone, email, contactMethod: phone } }));
    }
    return true;
  }
  function persist(targetStep = step, sourceDraft: Draft = draftRef.current, expectedVersion: number | undefined = versionRef.current, requestId: number | undefined = id): Promise<any> {
    const requestedDraftRevision = draftRevision.current;
    const run = async () => {
      // Keep the caller's explicit snapshot unless a real edit happened after it
      // was queued. Drain edits made during the network write before resolving,
      // using the returned id/version each time. targetStep remains this action's
      // navigation intent, paired with the draft revision it is saving.
      let writeDraft = draftRevision.current === requestedDraftRevision ? sourceDraft : draftRef.current;
      let queuedRequestId = idRef.current ?? requestId;
      let queuedVersion = versionRef.current ?? expectedVersion;
      setDraftKeyUrl();
      setSaving(true);
      setSaveState("Guardando…");
      setError("");
      try {
        while (true) {
          const normalizedEmail = normalizeEmailForDraft();
          const revisionAtWrite = draftRevision.current;
          const persistedStep = persistedCommercialWizardStep(writeDraft.serviceType, targetStep);
          const row: any = await save.mutateAsync({
            id: queuedRequestId,
            version: queuedVersion,
            idempotencyKey: queuedRequestId ? undefined : key,
            customerPartnerId: writeDraft.customerPartnerId,
            contactId: writeDraft.contactId,
            siteId: writeDraft.siteId,
            source: writeDraft.source,
            serviceType: writeDraft.serviceType,
            need: { ...writeDraft.need, wizardStep: persistedStep },
            parties: { ...writeDraft.parties, email: normalizedEmail },
            location: writeDraft.location,
          });
          if (!queuedRequestId) createdInSession.current = true;
          setId(Number(row.id));
          idRef.current = Number(row.id);
          setDraftUrl(Number(row.id));
          setVersion(Number(row.version));
          versionRef.current = Number(row.version);
          setDraftFromServer({
            ...draftRef.current,
            customerPartnerId: row.customerPartnerId == null ? draftRef.current.customerPartnerId : Number(row.customerPartnerId),
            need: { ...draftRef.current.need, wizardStep: persistedStep, ...(Number(row.need?.sourceGateVersion) === 1 ? { sourceGateVersion: 1 } : {}) },
          });
          setSaveState(row.prospectMatchReviewRequired
            ? "Guardado automáticamente · identidad pendiente de revisión de oficina"
            : row.customerPartnerId
              ? "Guardado automáticamente · entidad vinculada"
              : "Guardado automáticamente · solicitud guardada");
          await utils.commercialIntake.list.invalidate();
          await utils.commercialIntake.documents.invalidate({ id: Number(row.id) });
          if (draftRevision.current === revisionAtWrite) return row;
          writeDraft = draftRef.current;
          queuedRequestId = Number(row.id);
          queuedVersion = Number(row.version);
        }
      } catch (e: any) {
        setSaveState("No se pudo guardar");
        setError(e?.message ?? "Error de guardado");
        throw e;
      } finally {
        setSaving(false);
      }
    };
    const queued = persistQueue.current.then(run, run);
    persistQueue.current = queued.then(() => null, () => null);
    return queued;
  }
  async function chooseService(service: Service) {
    if (readOnlyOfficeReview) return;
    const updated = {
      ...draft,
      serviceType: service,
      need: { ...draft.need, wizardStep: 1 },
    };
    setDraft(updated);
    setDraftKeyUrl();
    setSaving(true);
    setError("");
    setSaveState("Guardando…");
    try {
      const row: any = await persist(1, updated, versionRef.current, idRef.current);
      if (!row) return;
      setDraftUrl(Number(row.id));
      setVersion(Number(row.version));
      versionRef.current = Number(row.version);
      if (Number(row.need?.sourceGateVersion) === 1) {
        setDraft(current => ({ ...current, need: { ...current.need, sourceGateVersion: 1 } }));
      }
      setStep(1);
    } catch (e: any) {
      setSaveState("No se pudo guardar");
      setError(e?.message ?? "Error de guardado");
    } finally {
      setSaving(false);
    }
  }
  useEffect(() => {
    if (!manualEntryRequested || activeStage !== "details") return;
    const target = manualSupplyRef.current;
    if (!target) return;
    target.open = true;
    target.scrollIntoView?.({ behavior: "smooth", block: "start" });
    (target.querySelector("input:not([type=checkbox])") as HTMLInputElement | null)?.focus({ preventScroll: true });
    setManualEntryRequested(false);
  }, [activeStage, manualEntryRequested]);
  async function goToManualSupplyEntry() {
    const targetStep = activeStages.findIndex(stage => stage.id === "details");
    if (targetStep < 0) return;
    try {
      if (step !== targetStep) {
        const row = await persist(targetStep);
        if (!row) return;
        setStep(targetStep);
      }
      setManualEntryRequested(true);
    } catch {
      // persist() exposes its failure in the wizard status; do not claim navigation succeeded.
    }
  }
  async function saveCombinedLocationAndRoof(targetStep: number) {
    let address = String(draftRef.current.location.address ?? "").trim();
    if (!address && draftRef.current.location.pending !== true) {
      const pendingDraft: Draft = { ...draftRef.current, location: { ...draftRef.current.location, pending: true } };
      setDraftSnapshot(pendingDraft);
      setSaveState("Ubicación sin verificar; queda pendiente para oficina");
    }
    let row = await persist(step, draftRef.current, versionRef.current, id);
    if (!row) return null;
    const currentLocation = request.data?.location ?? {};
    const alreadyConfirmed = isGeoConfirmationCurrent({
      confirmedAt: request.data?.geoConfirmedAt,
      confirmedAddress: currentLocation.address,
      currentAddress: address,
      latitude: request.data?.latitude,
      longitude: request.data?.longitude,
      currentLatitude: pin?.latitude,
      currentLongitude: pin?.longitude,
    });
    if (pin && address && !alreadyConfirmed) {
      if (confirmLocationInFlight.current) return null;
      confirmLocationInFlight.current = true;
      setConfirmingLocation(true);
      try {
        const confirmed: any = await confirmLocation.mutateAsync({
          id: Number(row.id), version: Number(row.version),
          latitude: pin.latitude, longitude: pin.longitude, address,
          source: locationSource,
        });
        setVersion(Number(confirmed.version));
        versionRef.current = Number(confirmed.version);
        const refreshed = await request.refetch();
        row = { ...row, ...refreshed?.data, version: Number(confirmed.version) };
        setDraft(current => ({ ...current, location: { ...current.location, pending: false } }));
        setSaveState("Ubicación confirmada y guardada");
      } finally {
        confirmLocationInFlight.current = false;
        setConfirmingLocation(false);
      }
    } else if (!pin) {
      const pendingDraft: Draft = { ...draftRef.current, location: { ...draftRef.current.location, pending: true } };
      setDraftSnapshot(pendingDraft);
      row = await persist(step, pendingDraft, versionRef.current, Number(row.id));
      if (!row) return null;
      setSaveState("Ubicación pendiente para oficina; no se ha marcado como confirmada");
    }
    if (draftRef.current.serviceType === "photovoltaic" && pin && roofSaveRef.current) {
      const savedRoof = await roofSaveRef.current(Number(versionRef.current));
      if (!savedRoof) {
        setError("No se confirmó el guardado de la cubierta. Revisa el detalle mostrado en Cubiertas; la ortofoto visible no garantiza que el servidor haya guardado la geometría.");
        return null;
      }
    }
    const finalRow = await persist(targetStep, draftRef.current, versionRef.current, Number(row.id));
    if (finalRow) setSaveState(pin && address ? "Ubicación confirmada y guardada" : "Ubicación pendiente para oficina; no se ha marcado como confirmada");
    return finalRow;
  }
  async function saveAndExit() {
    if (readOnlyOfficeReview) { setLocation("/commercial/requests"); return; }
    try {
      let row: any;
      if (activeStage === "location") {
        const editedDraft = draftWithExplicitBillEdits(draftRef.current);
        if (editedDraft !== draftRef.current) {
          setDraftSnapshot(editedDraft);
        }
        row = await saveCombinedLocationAndRoof(step);
      } else {
        const editedDraft = draftWithExplicitBillEdits(draftRef.current);
        if (editedDraft !== draftRef.current) setDraftSnapshot(editedDraft);
        row = await persist(step, editedDraft);
      }
      if (row) setLocation("/commercial/requests");
    } catch (error: any) {
      setError(error?.message ?? "No se pudo guardar la solicitud antes de salir.");
    }
  }

  async function next() {
    if (readOnlyOfficeReview) return;
    if (activeStage === "details" && draftRef.current.serviceType !== "ev_charger" && !validateRequiredContacts()) return;
    const detailsParties = draftRef.current.parties;
    const hasProspectIdentity = Boolean(String(detailsParties.clientName ?? detailsParties.contactName ?? "").trim());
    const hasValidProspectContacts = hasCommercialSubmissionContactDetails(detailsParties);
    if (activeStage === "details" && draftRef.current.serviceType !== "ev_charger" && !isPreviouslySubmittedResponse && !draftRef.current.customerPartnerId && hasProspectIdentity && hasValidProspectContacts && !["person", "organization"].includes(String(detailsParties.partyType ?? ""))) {
      setPartyTypeError("Indica si el cliente es persona u organización para identificar el prospecto.");
      window.setTimeout(() => partyTypeInputRef.current?.focus(), 0);
      return;
    }
    if (activeStage === "service" && !draft.serviceType) {
      setError("Elige un servicio para continuar.");
      return;
    }
    if (activeStage === "need" && draft.serviceType === "photovoltaic" && (!draft.need.kind || !draft.need.objective)) {
      setError("Selecciona el tipo de instalación y la necesidad principal para continuar.");
      return;
    }
    if (activeStage === "need" && draft.source === "pending" && sourceSelectionRequired) {
      setError("");
      setSourceError("Selecciona cómo nos has conocido para continuar.");
      window.setTimeout(() => sourceRadioRefs.current.direct?.focus(), 0);
      return;
    }
    try {
      const nextStep = Math.min(activeStages.length - 1, step + 1);
      const row = activeStage === "details"
        ? draftRef.current.serviceType === "ev_charger"
          ? await persist(nextStep)
          : await applyBillProposalsAndPersist(nextStep)
        : activeStage === "location"
          ? await saveCombinedLocationAndRoof(nextStep)
          : await persist(nextStep);
      if (row) setStep(nextStep);
    } catch (error: any) {
      setError(error?.message ?? "No se pudo guardar la ubicación y la cubierta. Revisa y vuelve a intentarlo.");
    }
  }

  function goToPriorStep(targetStep: number) {
    if (readOnlyOfficeReview) return;
    if (targetStep < 0 || targetStep >= step) return;
    void persist(targetStep)
      .then(row => { if (row) setStep(targetStep); })
      .catch(() => undefined);
  }

  async function upload(
    files: FileList | null,
    kind: string,
    category?: string
  ) {
    // FileList is live and the input is reset by onChange; copy it before persist().
    const selectedFiles = Array.from(files ?? []);
    if (!selectedFiles.length) return;
    let completed = 0;
    let uploadAttempted = false;
    let persistedRequestId: number | null = null;
    let refreshAttempted = false;
    try {
      const row = id ? { id } : await persist();
      if (!row) return;
      persistedRequestId = Number(row.id);
      for (const file of selectedFiles) {
        const params = new URLSearchParams({
          kind,
          ...(category ? { category } : {}),
        });
        uploadAttempted = true;
        const response = await fetch(
          `/api/commercial-requests/${row.id}/documents?${params}`,
          {
            method: "POST",
            credentials: "same-origin",
            headers: { "Content-Type": file.type, "X-File-Name": file.name },
            body: file,
          }
        );
        if (!response.ok) throw await commercialUploadHttpError(response);
        completed++;
      }
      await utils.commercialIntake.documents.invalidate({ id: persistedRequestId });
      refreshAttempted = true;
      await Promise.all([docs.refetch(), request.refetch()]);
    } catch (e: any) {
      if (persistedRequestId != null && uploadAttempted && !refreshAttempted) {
        refreshAttempted = true;
        await utils.commercialIntake.documents.invalidate({ id: persistedRequestId }).catch(() => undefined);
        await Promise.all([docs.refetch().catch(() => undefined), request.refetch().catch(() => undefined)]);
      }
      if (completed) {
        setError(completed === selectedFiles.length
          ? commercialUploadReadbackMessage(completed,e)
          : commercialUploadPartialMessage(completed,selectedFiles.length,e));
      } else setError(commercialUploadErrorMessage(e));
    }
  }
  async function searchAddress() {
    const address = String(draft.location.address ?? "").trim();
    const requestNumber = ++geocodeRequest.current;
    setMapError("");
    setChoices([]);
    setResolvedCartoCandidate(null);
    setAddressSearchPending(true);
    try {
    let googleFailure = "";
    try {
      const results = await geocode.mutateAsync({ address });
      if (requestNumber !== geocodeRequest.current) return;
      if (results.length) {
        setChoices(results.map((result: any) => ({ ...result, source: "google_geocoder" })));
        return;
      }
      googleFailure = "Google Maps no encontró coincidencias. ";
    } catch (e: any) {
      if (requestNumber !== geocodeRequest.current) return;
      const code = String(e?.message ?? "");
      googleFailure = code.includes("MAPS_CONFIGURATION")
        ? "Google Maps no está configurado. "
        : code.includes("MAPS_TIMEOUT") || code.includes("MAPS_UPSTREAM_HTTP") || code.includes("MAPS_NETWORK")
          ? "Google Maps no está disponible ahora. "
          : "Google Maps no pudo consultar la dirección. ";
    }
    try {
      const results = await searchCartoCiudad(address);
      if (requestNumber !== geocodeRequest.current) return;
      setChoices(results);
      setDraft(d => ({ ...d, location: { ...d.location, pending: true } }));
      setMapError(results.length
        ? `${googleFailure}Se muestran propuestas de CartoCiudad/CNIG; selecciona una para resolverla y confirmarla.`
        : `${googleFailure}CartoCiudad no encontró propuestas. Puedes ajustar el pin en el mapa o dejar la ubicación pendiente.`);
    } catch {
      if (requestNumber !== geocodeRequest.current) return;
      setDraft(d => ({ ...d, location: { ...d.location, pending: true } }));
      setMapError(`${googleFailure}No se pudo consultar CartoCiudad. Puedes marcar el pin sobre PNOA o dejar la ubicación pendiente.`);
    }
    } finally {
      if (requestNumber === geocodeRequest.current) setAddressSearchPending(false);
    }
  }
  async function searchCartoCiudadAddress() {
    const address = String(draft.location.address ?? "").trim();
    const requestNumber = ++geocodeRequest.current;
    setMapError("");
    setChoices([]);
    setResolvedCartoCandidate(null);
    setPin(null);
    setDraft(d => ({ ...d, location: { ...d.location, pending: true } }));
    setAddressSearchPending(true);
    if (address.length < 5) {
      setMapError("Introduce una dirección de al menos cinco caracteres para buscarla con CartoCiudad.");
      setAddressSearchPending(false);
      return;
    }
    try {
      const results = await searchCartoCiudad(address);
      if (requestNumber !== geocodeRequest.current) return;
      setChoices(results);
      setMapError(results.length
        ? "CartoCiudad ofrece ubicaciones candidatas. Selecciona una para resolverla; todavía no se ha colocado ningún pin."
        : "CartoCiudad no resolvió el emplazamiento. La ubicación queda pendiente y no se ha colocado ningún pin.");
    } catch {
      if (requestNumber !== geocodeRequest.current) return;
      setMapError("No se pudo consultar CartoCiudad. La ubicación queda pendiente y no se ha colocado ningún pin.");
    } finally {
      if (requestNumber === geocodeRequest.current) setAddressSearchPending(false);
    }
  }
  function normalizedAddressKey(address: string) {
    return address.trim().replace(/\s+/g, " ").toLocaleLowerCase("es");
  }
  async function autoResolveCartoCiudadAddress(address: string) {
    const normalizedAddress = address.trim();
    if (!normalizedAddress) return;
    const requestNumber = ++geocodeRequest.current;
    setAddressSearchPending(true);
    setMapError("");
    setChoices([]);
    setResolvedCartoCandidate(null);
    try {
      const candidates = await searchCartoCiudad(normalizedAddress);
      if (requestNumber !== geocodeRequest.current) return;
      if (candidates.length !== 1) {
        setChoices(candidates);
        setDraft(current => ({ ...current, location: { ...current.location, pending: true } }));
        setMapError(candidates.length
          ? "Hay varias propuestas CartoCiudad. Elige la dirección correcta; todavía no se ha colocado el pin."
          : "CartoCiudad no encontró una propuesta. Busca manualmente, ajusta el pin o deja la ubicación pendiente.");
        return;
      }
      const resolved = await resolveCartoCiudadCandidate(candidates[0]);
      if (requestNumber !== geocodeRequest.current) return;
      setResolvedCartoCandidate(resolved);
      setDraft(current => ({ ...current, location: { ...current.location, pending: true } }));
      setMapError("CartoCiudad propone una ubicación. Revisa el mapa y confirma explícitamente antes de guardar.");
    } catch {
      if (requestNumber !== geocodeRequest.current) return;
      setDraft(current => ({ ...current, location: { ...current.location, pending: true } }));
      setMapError("No se pudo resolver la dirección automáticamente con CartoCiudad. Busca manualmente, ajusta el pin o deja la ubicación pendiente.");
    } finally {
      if (requestNumber === geocodeRequest.current) setAddressSearchPending(false);
    }
  }
  function startAutoResolveForAddress(address: string, requestId = Number(id)) {
    const normalizedAddress = normalizedAddressKey(address);
    if (!normalizedAddress || !Number.isSafeInteger(requestId) || requestId <= 0) return;
    const key = `${requestId}:${normalizedAddress}`;
    if (autoResolvedAddresses.current.has(key)) return;
    // Claim before I/O: StrictMode replays effects, but each semantic proposal resolves once.
    autoResolvedAddresses.current.add(key);
    void autoResolveCartoCiudadAddress(address.trim());
  }
  useEffect(() => {
    const requestId = Number(id);
    const normalizedAddress = normalizedAddressKey(currentAddress);
    const savedAddress = normalizedAddressKey(String(request.data?.location?.address ?? ""));
    const hasReliableSavedCoordinates = savedAddress === normalizedAddress &&
      Number.isFinite(Number(request.data?.latitude)) && Number.isFinite(Number(request.data?.longitude)) &&
      request.data?.latitude != null && request.data?.longitude != null;
    if (activeStage !== "location" || !hydrated.current || !Number.isSafeInteger(requestId) || requestId <= 0 ||
      !normalizedAddress || (selectedSuggestion === currentAddress && pin) ||
      (request.data?.geoConfirmedAt && hasReliableSavedCoordinates)) return;
    // Automatic lookup is only for the initial proposed address when entering the stage.
    // Manual text edits do not trigger it; users can explicitly use either Search action.
    startAutoResolveForAddress(currentAddress, requestId);
  }, [activeStage, id, request.data?.geoConfirmedAt, request.data?.latitude, request.data?.longitude]);

  async function chooseAddressCandidate(choice: CartoCiudadCandidate | { source: "google_geocoder"; address: string; placeId: string; latitude: number; longitude: number }) {
    if (choice.source !== "cartociudad") {
      geocodeRequest.current += 1;
      setMapError("");
      setResolvedCartoCandidate(null);
      setDraft(d => ({ ...d, location: { ...d.location, pending: false } }));
      setPin({ latitude: choice.latitude, longitude: choice.longitude });
      setLocationSource(choice.source ?? "google_geocoder");
      setSelectedSuggestion(choice.address);
      setChoices([]);
      return;
    }
    const requestNumber = ++geocodeRequest.current;
    setAddressSearchPending(true);
    setMapError("");
    setResolvedCartoCandidate(null);
    setPin(null);
    setDraft(d => ({ ...d, location: { ...d.location, pending: true } }));
    try {
      const resolved = await resolveCartoCiudadCandidate(choice);
      if (requestNumber !== geocodeRequest.current) return;
      setResolvedCartoCandidate(resolved);
      setChoices([]);
      setMapError("CartoCiudad resolvió la propuesta. Revisa el centro de PNOA y confirma explícitamente para colocar el pin.");
    } catch {
      if (requestNumber !== geocodeRequest.current) return;
      setMapError("CartoCiudad no pudo resolver coordenadas válidas para esta propuesta. No se ha colocado ni confirmado ningún pin; elige otra propuesta o ajústalo manualmente sobre PNOA.");
    } finally {
      if (requestNumber === geocodeRequest.current) setAddressSearchPending(false);
    }
  }
  function confirmResolvedCartoCandidate() {
    const candidate = resolvedCartoCandidate;
    if (!candidate || confirmingLocation || confirmLocationInFlight.current) return;
    geocodeRequest.current += 1;
    setDraft(d => ({ ...d, location: { ...d.location, address: candidate.address, installationAddress: candidate.address, addressDetails: { ...(candidate.addressDetails ?? {}), reference: candidate.placeId, provenance: "cartociudad" }, pending: false } }));
    setPin({ latitude: candidate.latitude, longitude: candidate.longitude });
    setLocationSource("cartociudad");
    setSelectedSuggestion(candidate.address);
    setResolvedCartoCandidate(null);
    setChoices([]);
    setMapError("Ubicación CartoCiudad confirmada como propuesta; revisa el pin antes de guardar.");
  }

  async function submit() {
    if (readOnlyOfficeReview || submitInFlight.current || !validateRequiredContacts()) return;
    submitInFlight.current = true;
    setSubmitting(true);
    try {
      const row = await persist();
      if (!row) return;
      await send.mutateAsync({
        id: Number(row.id),
        version: Number(row.version),
      });
      await utils.commercialIntake.list.invalidate();
      setSaveState("Enviada a oficina");
    } catch (e: any) {
      setError(e?.message === "COMMERCIAL_REQUEST_REQUIRED_FIELDS_MISSING"
        ? "No se envió la solicitud. Revisa los datos obligatorios de contacto y del expediente."
        : e?.message ?? "No se pudo enviar.");
    } finally {
      submitInFlight.current = false;
      setSubmitting(false);
    }
  }
  const holderContractorDifference = hasNewPvHolderContractorDifference({
    serviceType: draft.serviceType,
    kind: draft.need.kind,
    objective: draft.need.objective,
    supplyHolderName: draft.parties.supplyHolderName,
    contractingPartyName: draft.parties.contractingPartyName,
  });
  const locationHistoryEntries = (locationHistory.data ?? []).flatMap((event: any) => {
    let details = event.details;
    if (typeof details === "string") { try { details = JSON.parse(details); } catch { details = {}; } }
    const change = details?.location;
    return change?.action === "confirmed" && change.after
      ? [{ ...change, id: event.id, createdAt: event.createdAt, actorName: event.actorName }]
      : [];
  });
  const pendingItems = commercialPendingItems({
    location: draft.location,
    geoConfirmed: isGeoConfirmationCurrent({
      confirmedAt: request.data?.geoConfirmedAt,
      confirmedAddress: request.data?.location?.address,
      currentAddress: draft.location.address,
      latitude: request.data?.latitude,
      longitude: request.data?.longitude,
      currentLatitude: pin?.latitude,
      currentLongitude: pin?.longitude,
    }),
    photoStatus: draft.need.photoStatus,
    hasCategorizedPhotos: (docs.data ?? []).some((doc: any) => Boolean(doc.captureCategory)),
    serviceType: draft.serviceType,
    supplies: draft.parties.supplies ?? [],
    holderContractorDifference,
  });
  const requiredFieldsReady = Boolean(
    String(draft.serviceType ?? "").trim() &&
      (String(draft.location.address ?? "").trim() || draft.location.pending === true) &&
      hasCommercialSubmissionIdentity({
        customerPartnerId: draft.customerPartnerId,
        parties: draft.parties,
      }) &&
      (isPreviouslySubmittedResponse
        ? hasCommercialSubmissionContactMethod(draft.parties)
        : hasCommercialSubmissionContactDetails(draft.parties)) &&
      String(draft.need.description ?? "").trim()
  );
  if (!user) return null;
  if (!["sales", "admin", "admin_manager", "sales_admin"].includes(user.role))
    return (
      <CommercialPresentationFrame>
        <p className="p-8 text-sm text-muted-foreground">
          No tienes permiso para crear solicitudes comerciales.
        </p>
        <div className="px-8 pb-8">
          <Button size="touch" variant="outline" onClick={() => setLocation("/")}>
            Volver al inicio
          </Button>
        </div>
      </CommercialPresentationFrame>
    );
  if (requestedId != null && request.isLoading)
    return <CommercialPresentationFrame><main className="mx-auto max-w-3xl space-y-4 p-6" aria-busy="true"><p role="status">Cargando solicitud…</p><Button size="touch" variant="outline" onClick={() => setLocation("/")}>Volver al inicio</Button></main></CommercialPresentationFrame>;
  if (requestedId != null && request.isError)
    return <CommercialPresentationFrame><main className="mx-auto max-w-3xl space-y-4 p-6"><h1 className="text-2xl font-semibold">Solicitud no disponible</h1><p role="alert" className="text-sm text-muted-foreground">No se pudo abrir esta solicitud. Comprueba que esté en «Mis solicitudes» asignadas a tu usuario o pide a oficina que te facilite acceso por el procedimiento habitual.</p><Button size="touch" variant="outline" onClick={() => setLocation("/commercial/requests")}>Volver a Mis solicitudes</Button></main></CommercialPresentationFrame>;
  return (
    <CommercialPresentationFrame headerContent={
<div className="commercial-wizard-banner-inner mx-auto w-full max-w-[1280px] px-4 py-4 sm:px-5 md:px-7">
        <div className="commercial-brand-lockup flex min-w-0 items-center gap-3">
          <img src="/brand/ods-logo-commercial-white.png" alt="ODS Energy" className="commercial-logo-white h-8 w-auto max-w-[148px] object-contain" />
          <span className="text-sm font-medium tracking-wide">Solicitud comercial</span>
        </div>
        <div className={activeStage === "need" && draft.serviceType === "photovoltaic" ? "mt-3 commercial-need-header-stack" : activeStage === "need" && draft.serviceType === "ev_charger" ? "mt-3 commercial-ev-need-header-stack" : "mt-3"}>
        <header className="commercial-flow-header flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 max-w-full">
            <button
              onClick={() => setLocation("/commercial/requests")}
              className="text-sm text-ods-navigation underline"
            >
              Mis solicitudes
            </button>
            <h1 className="mt-1 break-words text-2xl font-semibold">
              {activeStage === "details" ? "¿Cómo quieres añadir los datos de la factura?" : activeStage === "need" ? (draft.serviceType === "photovoltaic" ? "Instalación" : "Instalación y necesidad") : activeStage === "location" ? "Ubicación y cubierta" : activeStage === "summary" ? "Resumen de solicitud" : activeStage === "photos" ? "Fotografías de la instalación" : activeStage === "service" ? "Nueva solicitud comercial" : "Solicitud comercial"}
            </h1>
            {saveState && <p role="status" className="text-sm text-muted-foreground">
              {saveState || (activeStage === "need"
                ? "Se guarda al avanzar; puedes guardar en cada paso."
                : "El borrador se guarda al avanzar y puedes guardarlo en cualquier paso.")}
            </p>}
          </div>
          <Button
            size="touch"
            variant="outline"
            disabled={saving}
            onClick={() => void saveAndExit()}
          >
            {readOnlyOfficeReview ? "Volver a solicitudes" : "Guardar y salir"}
          </Button>
        </header>
        {customerContextNotice && <p role="status" className="text-sm">{customerContextNotice}</p>}
        {readOnlyOfficeReview && <p role="status" className="mb-3 rounded-lg bg-muted/40 p-3 text-sm">Solicitud formalizada · vista de solo lectura. Para continuar, usa la revisión de oficina.</p>}
        <nav aria-label="Progreso" className={`commercial-flow-progress min-w-0 space-y-2 border-b pb-3 ${activeStage === "need" && draft.serviceType === "photovoltaic" ? "commercial-need-progress" : ""}`}>
          <div className="hidden min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-1 sm:flex">
            <span data-testid="wizard-desktop-current-step" className="min-w-0 truncate text-sm font-semibold">{activeStages[step]?.label ?? "Servicio"}</span>
            <span className="shrink-0 text-xs font-medium text-muted-foreground">Paso {step + 1} de {activeStages.length}</span>
          </div>
          <div className="flex min-w-0 items-center justify-between gap-3 sm:hidden">
            <div aria-live="polite" aria-atomic="true" className="min-w-0">
              <span className="block text-xs font-medium text-muted-foreground">Paso {step + 1} de {activeStages.length}</span>
              <span data-testid="wizard-current-step" className="block truncate text-sm font-semibold">{activeStages[step]?.label ?? "Servicio"}</span>
            </div>
            {!readOnlyOfficeReview && step > 0 && <label className="sr-only" htmlFor="wizard-previous-step">Ir a un paso anterior</label>}
            {!readOnlyOfficeReview && step > 0 && (
              <select
                id="wizard-previous-step"
                aria-label="Ir a un paso anterior"
                data-testid="wizard-mobile-step-select"
                value=""
                onChange={event => goToPriorStep(Number(event.currentTarget.value))}
                className="commercial-progress-step-select min-h-11 max-w-[10rem] rounded-lg px-2 text-sm"
              >
                <option value="" disabled>Cambiar paso</option>
                {activeStages.map((stage, i) => (
                  <option key={stage.id} value={i} disabled={i >= step}>{i + 1}. {stage.label}</option>
                ))}
              </select>
            )}
          </div>
          <div data-testid="wizard-progress-track" className="flex min-w-0 gap-1" aria-hidden="true">
            {activeStages.map((stage, i) => <span key={stage.id} className={`h-1.5 min-w-0 flex-1 rounded-full ${i <= step ? "bg-primary" : "bg-muted"}`} />)}
          </div>
          {!readOnlyOfficeReview && <div data-testid="wizard-stage-labels" className="hidden min-w-0 grid-cols-1 gap-1 sm:flex sm:flex-nowrap sm:items-center">
            {activeStages.map((stage, i) => (
              <button key={stage.id} aria-label={`${stage.label}${i > step ? " (pendiente)" : ""}`} aria-current={step === i ? "step" : undefined} disabled={i > step} onClick={() => goToPriorStep(i)} className={`min-h-10 min-w-0 max-w-full justify-self-stretch rounded-md px-2 text-left text-xs whitespace-normal leading-tight sm:min-w-10 sm:justify-self-auto sm:text-center ${step === i ? "bg-primary font-semibold text-primary-foreground" : i < step ? "text-muted-foreground hover:bg-muted" : "cursor-default text-muted-foreground/70"}`}><span className="flex min-w-0 items-center gap-2"><span className="grid size-6 shrink-0 place-items-center rounded-full border text-[11px]">{i + 1}</span><span className="sr-only">{stage.label}</span></span></button>
            ))}
          </div>}
        </nav>
        </div>
      </div>
    }>
      <main className={`commercial-wizard-shell mx-auto min-w-0 ${activeStage === "need" ? "space-y-3 commercial-need-stage" : "space-y-5"} p-4 sm:p-5 md:p-7 ${activeStage === "details" ? "max-w-[960px]" : ["service", "need", "location", "roof", "photos"].includes(activeStage) ? "max-w-7xl" : "max-w-4xl"}`}>

        <section id="commercial-wizard-step-content" className={`${activeStage === "need" ? "min-h-0" : "min-h-[430px]"} min-w-0 rounded-2xl border bg-card p-4 shadow-sm sm:p-5 md:p-6`}>
          {activeStage === "service" && (
            <div className="space-y-4">
              <div><h2 className="text-2xl font-semibold tracking-tight">¿Qué necesita el cliente?</h2><p className="mt-1 text-sm text-muted-foreground">Elige el servicio que mejor describe lo que busca.</p></div>
              <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {([
                  ["photovoltaic", "Fotovoltaica", "Generar y aprovechar energía solar", "p1-1-fotovoltaica"],
                  ["ev_charger", "Cargador de vehículo eléctrico", "Cargar el vehículo en casa o en la empresa", "p1-2-cargador-ve"],
                  ["maintenance", "Mantenimiento", "Revisar y conservar una instalación", "p1-3-mantenimiento"],
                ] as const).map(([value, label, description, image]) => {
                  const selected = draft.serviceType === value;
                  return <button key={value} aria-pressed={selected} onClick={() => void chooseService(value)} className={`relative overflow-hidden rounded-2xl border bg-card text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${selected ? "border-primary bg-primary/5 ring-2 ring-primary" : "hover:border-primary/60"}`}>
                    <img src={`/commercial/service/${image}-1600.webp`} srcSet={`/commercial/service/${image}-800.webp 800w, /commercial/service/${image}-1600.webp 1600w`} sizes="(min-width: 1280px) 32vw, (min-width: 640px) 48vw, 100vw" alt={label} loading="lazy" className="aspect-[2/1] w-full object-cover" />
                    <span className="flex min-h-24 items-start justify-between gap-3 p-4"><span><b className="block text-lg">{label}</b><span className="mt-1 block text-sm text-muted-foreground">{description}</span></span>{selected && <span aria-hidden="true" className="grid size-7 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground">✓</span>}</span>
                  </button>;
                })}
              </div>
            </div>
          )}
          {activeStage === "need" && (
            <div className={draft.serviceType === "photovoltaic" ? "space-y-4 commercial-photovoltaic-need" : "space-y-4"}>
              {draft.serviceType !== "photovoltaic" && <h2 className="text-xl font-semibold">{draft.serviceType === "ev_charger" ? "Datos del cargador" : "Datos de mantenimiento"}</h2>}
              {draft.serviceType === "photovoltaic" && (
                <>
                  <div className="space-y-5">
                    <fieldset className="commercial-choice-section">
                      <legend className="commercial-choice-heading"><span><b className="commercial-need-step-number">01</b> ¿Qué tipo de instalación es?</span><small>Elige una opción para orientar la propuesta.</small></legend>
                      <div data-testid="installation-type-grid" className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        {(
                          [
                            ["particular", "Vivienda particular"],
                            ["community", "Comunidad"],
                            ["industry", "Industria"],
                            ["orchard", "Huerto solar"],
                          ] as const
                        ).map(([value, label]) => (
                          <button
                            type="button"
                            key={value}
                            aria-pressed={draft.need.kind === value}
                            onClick={() =>
                              setDraft(d => ({
                                ...d,
                                need: { ...d.need, kind: value },
                              }))
                            }
                            className={`commercial-choice-card commercial-installation-card min-h-11 rounded-2xl border p-0 text-left focus-visible:ring-2 focus-visible:ring-ring ${draft.need.kind === value ? "is-selected" : ""}`}
                          >
                            <img src={`/commercial/service/${({particular:"p2a-4-vivienda-particular",community:"p2a-5-comunidad",industry:"p2a-6-industria",orchard:"p2a-7-huerto-solar"} as Record<string,string>)[value]}-800.webp`} srcSet={`/commercial/service/${({particular:"p2a-4-vivienda-particular",community:"p2a-5-comunidad",industry:"p2a-6-industria",orchard:"p2a-7-huerto-solar"} as Record<string,string>)[value]}-800.webp 800w, /commercial/service/${({particular:"p2a-4-vivienda-particular",community:"p2a-5-comunidad",industry:"p2a-6-industria",orchard:"p2a-7-huerto-solar"} as Record<string,string>)[value]}-1600.webp 1600w`} sizes="(min-width: 768px) 40vw, 100vw" alt={label} loading="lazy" className="commercial-choice-photo aspect-[2/1] w-full object-cover lg:aspect-[5/2]" />
                            <span className="commercial-choice-card-caption"><span><b className="block text-lg leading-snug">{label}</b>
                            <span className="mt-1 block text-sm">
                              {value === "particular"
                                ? "Autoconsumo en vivienda"
                                : value === "community"
                                  ? "Cubierta y consumos compartidos"
                                  : value === "industry"
                                    ? "Instalación para actividad productiva"
                                    : "Generación en terreno"}
                            </span></span>{draft.need.kind === value && <span aria-label="Seleccionada" className="commercial-choice-check">✓</span>}</span>
                          </button>
                        ))}
                      </div>
                    </fieldset>
                    <fieldset className="commercial-choice-section">
                      <legend className="commercial-choice-heading"><span><b className="commercial-need-step-number">02</b> ¿Qué necesitas hacer?</span><small>Después podrás concretar los datos y consumos.</small></legend>
                      <div data-testid="installation-need-grid" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {(
                          [
                            ["new", "Nueva instalación"],
                            ["expand", "Ampliación"],
                            ["batteries", "Añadir baterías"],
                          ] as const
                        ).map(([value, label]) => (
                          <button
                            type="button"
                            key={value}
                            aria-pressed={draft.need.objective === value}
                            onClick={() =>
                              setDraft(d => ({
                                ...d,
                                need: { ...d.need, objective: value },
                              }))
                            }
                            className={`commercial-choice-card commercial-objective-card min-h-11 rounded-2xl border p-0 text-left focus-visible:ring-2 focus-visible:ring-ring ${draft.need.objective === value ? "is-selected" : ""}`}
                          >
                            <img src={`/commercial/service/${({new:"p2b-8-nueva-instalacion",expand:"p2b-9-ampliacion",batteries:"p2b-10-anadir-baterias"} as Record<string,string>)[value]}-800.webp`} srcSet={`/commercial/service/${({new:"p2b-8-nueva-instalacion",expand:"p2b-9-ampliacion",batteries:"p2b-10-anadir-baterias"} as Record<string,string>)[value]}-800.webp 800w, /commercial/service/${({new:"p2b-8-nueva-instalacion",expand:"p2b-9-ampliacion",batteries:"p2b-10-anadir-baterias"} as Record<string,string>)[value]}-1600.webp 1600w`} sizes="(min-width: 768px) 32vw, 100vw" alt={label} loading="lazy" className="commercial-choice-photo aspect-[5/2] w-full object-cover" />
                            <span className="commercial-choice-card-caption"><span><b className="block text-base leading-snug">{label}</b>
                            <span className="mt-1 block text-sm">
                              {value === "new"
                                ? "Empezar desde cero"
                                : value === "expand"
                                  ? "Aumentar capacidad existente"
                                  : "Incorporar almacenamiento"}
                            </span></span>{draft.need.objective === value && <span aria-label="Seleccionada" className="commercial-choice-check">✓</span>}</span>
                          </button>
                        ))}
                      </div>
                    </fieldset>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={Boolean(draft.need.batteryComplement)}
                        onChange={e =>
                          setDraft(d => ({
                            ...d,
                            need: {
                              ...d.need,
                              batteryComplement: e.target.checked,
                            },
                          }))
                        }
                      />
                      Interés adicional en baterías
                    </label>
                    {["expand", "batteries"].includes(draft.need.objective) && (
                      <div className="grid gap-3 rounded-xl border p-4 sm:grid-cols-2">
                        <b className="sm:col-span-2">¿Qué tiene instalado?</b>
                        <Input controlSize="touch"
                          placeholder="Paneles / potencia"
                          value={draft.need.existingPanels ?? ""}
                          onChange={e =>
                            setDraft(d => ({
                              ...d,
                              need: {
                                ...d.need,
                                existingPanels: e.target.value,
                              },
                            }))
                          }
                        />
                        <Input controlSize="touch"
                          placeholder="Inversor / equipos"
                          value={draft.need.existingInverter ?? ""}
                          onChange={e =>
                            setDraft(d => ({
                              ...d,
                              need: {
                                ...d.need,
                                existingInverter: e.target.value,
                              },
                            }))
                          }
                        />
                        <Input controlSize="touch"
                          placeholder="Baterías existentes"
                          value={draft.need.existingBatteries ?? ""}
                          onChange={e =>
                            setDraft(d => ({
                              ...d,
                              need: {
                                ...d.need,
                                existingBatteries: e.target.value,
                              },
                            }))
                          }
                        />
                        <Input controlSize="touch"
                          aria-label="Potencia FV existente"
                          placeholder="Potencia FV existente (kWp)"
                          value={draft.need.existingPower ?? ""}
                          onChange={e =>
                            setDraft(d => ({
                              ...d,
                              need: { ...d.need, existingPower: e.target.value },
                            }))
                          }
                        />
                        <Textarea
                          aria-label="Descripción de la instalación existente"
                          placeholder="Descripción conocida de la instalación existente"
                          value={draft.need.existingDescription ?? ""}
                          onChange={e =>
                            setDraft(d => ({
                              ...d,
                              need: { ...d.need, existingDescription: e.target.value },
                            }))
                          }
                        />
                        <label className="grid gap-1 text-sm sm:col-span-2">
                          Recuperar instalación conocida del cliente
                          <select
                            aria-label="Instalación conocida"
                            className="h-11 w-full min-w-0 max-w-full rounded-md border bg-background px-3 xl:h-10"
                            value={draft.need.knownInstallationId ?? ""}
                            disabled={!draft.customerPartnerId || knownInstallations.isLoading}
                            onChange={e => {
                              const installation = (knownInstallations.data?.items ?? []).find(
                                (item: any) => String(item.id) === e.target.value
                              );
                              setDraft(current => ({
                                ...current,
                                need: {
                                  ...current.need,
                                  knownInstallationId: installation ? Number(installation.id) : undefined,
                                  knownInstallationLabel: installation
                                    ? [installation.installationCode || installation.name || `Instalación ${installation.id}`, installation.address]
                                        .filter(Boolean).join(" · ")
                                    : undefined,
                                  existingPower: installation?.installedPowerKwp != null
                                    ? String(installation.installedPowerKwp)
                                    : current.need.existingPower,
                                  existingDescription: installation?.workDescription || current.need.existingDescription,
                                },
                                location: !String(current.location.address ?? "").trim() && installation?.address
                                  ? { ...current.location, address: installation.address }
                                  : current.location,
                              }));
                            }}
                          >
                            <option value="">Describir manualmente / sin instalación vinculada</option>
                            {(knownInstallations.data?.items ?? []).map((item: any) => (
                              <option key={item.id} value={item.id}>
                                {[item.installationCode || item.name || `Instalación ${item.id}`, item.address].filter(Boolean).join(" · ")}
                              </option>
                            ))}
                          </select>
                        </label>
                        {!draft.customerPartnerId && (
                          <p className="text-xs text-muted-foreground sm:col-span-2">
                            Para recuperar una instalación, selecciona primero el cliente en «Datos» y vuelve a esta pantalla. Sin un cliente vinculado puedes describirla manualmente.
                          </p>
                        )}
                        {draft.customerPartnerId && !knownInstallations.isLoading && !(knownInstallations.data?.items ?? []).length && (
                          <p className="text-xs text-muted-foreground sm:col-span-2">
                            No hay instalaciones vinculadas disponibles para este cliente; puedes describir la existente manualmente.
                          </p>
                        )}
                        <div className="sm:col-span-2">
                          <p className="mb-2 text-sm">
                            Adjunta aquí el proyecto, la memoria, fotografías y etiquetas de equipos existentes. Los originales privados aparecerán en el resumen de oficina.
                          </p>
                          <CommercialDocumentManager
                            requestId={id ?? 0}
                            version={version ?? Number(request.data?.version ?? 1)}
                            documents={docs.data ?? []}
                            kind="supporting"
                            onVersion={v => setVersion(v)}
                            refresh={async () => {
                              await Promise.all([docs.refetch(), request.refetch()]);
                            }}
                            readOnly={!id}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
              {draft.serviceType === "ev_charger" && (
                <div className="ev-need-experience space-y-6">
                  <section aria-labelledby="ev-invoice-title" className="space-y-3 rounded-2xl bg-muted/30 p-4 sm:p-5">
                    <div>
                      <h3 id="ev-invoice-title" className="text-lg font-semibold">¿Quieres añadir tu factura eléctrica?</h3>
                      <p className="mt-1 text-sm text-muted-foreground">Es opcional. Puede ayudar a la revisión, pero no necesitas conocer el CUPS ni esperar a la lectura para configurar el Trydan y continuar.</p>
                    </div>
                    {id ? (
                      <CommercialDocumentManager
                        requestId={id}
                        version={version ?? Number(request.data?.version ?? 1)}
                        documents={docs.data ?? []}
                        kind="electricity_bill"
                        contextualInvoiceFlow
                        onVersion={nextVersion => { versionRef.current = nextVersion; setVersion(nextVersion); }}
                        refresh={async () => { await Promise.all([docs.refetch(), request.refetch()]); }}
                      />
                    ) : (
                      <p className="text-sm">Puedes continuar sin factura y añadirla después desde la solicitud guardada.</p>
                    )}
                    <p role="status" className="text-xs text-muted-foreground">{(docs.data ?? []).some((doc: any) => doc.documentKind === "electricity_bill") ? "Factura guardada en la solicitud. La lectura y revisión pueden seguir en segundo plano; no cambian el precio del catálogo." : "Sin factura añadida. Puedes continuar sin ella; no se bloqueará el recorrido."}</p>
                  </section>
                  <section aria-labelledby="ev-site-title" className="space-y-3">
                    <div><h3 id="ev-site-title" className="text-lg font-semibold">¿Dónde quieres cargar?</h3><p className="text-sm text-muted-foreground">Elige el emplazamiento. Toda la fotografía es seleccionable.</p></div>
                    <div className="grid gap-4 md:grid-cols-2">
                      {([
                        ["single_house", "Tu punto de recarga en casa", "Punto privado en vivienda unifamiliar", "home-charging-context.webp", "home"],
                        ["community", "Recarga en tu plaza de garaje", "Punto individual en garaje comunitario", "community-garage-context.webp", "community"],
                      ] as const).map(([value, title, description, photo, siteKind]) => {
                        const selected = draft.need.siteKind === value;
                        return <button key={value} type="button" aria-pressed={selected} onClick={() => setDraft(d => {
                          const nextNeed = { ...d.need, siteKind: value };
                          return { ...d, need: reconcileEvCatalogOption(nextNeed, evCatalog.data ?? []) };
                        })} className={`commercial-choice-card ev-site-card ${selected ? "is-selected" : ""}`}>
                          <img src={`/commercial/ev/${photo}`} srcSet={`/commercial/ev/${photo.replace(".webp", "-800.webp")} 800w, /commercial/ev/${photo} 1600w`} sizes="(max-width: 767px) 100vw, 50vw" alt="" aria-hidden="true" loading="lazy" className="ev-site-photo" />
                          <span className="commercial-choice-card-caption"><span><b className="block text-lg">{title}</b><span className="mt-1 block text-sm">{description}</span></span>{selected && <span aria-label="Seleccionada" className="commercial-choice-check">✓</span>}</span>
                        </button>;
                      })}
                    </div>
                  </section>
                  <fieldset className="space-y-3" aria-describedby="ev-supply-help">
                    <legend className="text-lg font-semibold">¿Qué suministro tienes?</legend>
                    <p id="ev-supply-help" className="text-sm text-muted-foreground">La potencia disponible depende del vehículo y de la instalación. Si no lo sabes, lo revisamos contigo.</p>
                    <div className="grid gap-3 sm:grid-cols-3">
                      {([
                        ["mono", "Monofásico", "Hasta 7,4 kW", "Una fase · esquema simplificado"],
                        ["tri", "Trifásico", "Hasta 22 kW", "Tres fases · esquema simplificado"],
                        ["unknown", "No lo sé", "Lo revisamos contigo", "Continúa sin elegir una potencia"],
                      ] as const).map(([value, label, detail, aria]) => {
                        const selected = (draft.need.phase ?? "unknown") === value;
                        return <button key={value} type="button" aria-pressed={selected} onClick={() => setDraft(d => {
                          const nextNeed = { ...d.need, phase: value };
                          return { ...d, need: reconcileEvCatalogOption(nextNeed, evCatalog.data ?? []) };
                        })} className={`ev-supply-card ${selected ? "is-selected" : ""}`}>
                          <svg aria-hidden="true" viewBox="0 0 96 52" className="ev-supply-illustration"><path d="M12 8v36M84 8v36M12 18h72M12 34h72"/><path d={value === "mono" ? "M48 18v16" : value === "tri" ? "M30 18v16M48 18v16M66 18v16" : "M48 17v9m0 6v2"}/></svg>
                          <span className="ev-supply-label">{label}</span><span className="ev-supply-power">{detail}</span><span className="sr-only">{aria}</span>
                        </button>;
                      })}
                    </div>
                  </fieldset>
                  <div className="ev-offer-layout">
                    <article className="ev-product-card" aria-labelledby="ev-product-heading">
                      <div className="ev-product-photo-wrap"><img src="/commercial/ev/trydan-product.webp" alt="V2C Trydan, unidad de recarga mural con cable Tipo 2" className="ev-product-photo" loading="lazy" /></div>
                      <div className="ev-product-copy"><p className="text-xs font-semibold uppercase tracking-[.15em] text-muted-foreground">V2C · catálogo vigente</p><h3 id="ev-product-heading" className="mt-1 text-xl font-semibold">Trydan</h3><p className="mt-1 text-sm">Conexión Tipo 2 · manguera de 5 m</p>
                        {evCatalog.isError && <p role="alert" className="mt-3 text-sm text-destructive">No se pudo cargar el catálogo vigente. El precio queda pendiente.</p>}
                        {evCatalog.isLoading && <p className="mt-3 text-sm text-muted-foreground">Consultando catálogo…</p>}
                        {!evCatalog.isLoading && !evCatalog.isError && (() => {
                          const selected = resolveEvCatalogOption(draft.need.siteKind, draft.need.phase, evCatalog.data ?? []);
                          return selected ? <><p className="mt-3 text-sm">{selected.label}</p><p className="mt-1 text-2xl font-bold">{new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(selected.priceCents / 100)}<span className="ml-2 text-xs font-medium">IVA incluido · orientativo</span></p><p className="mt-2 text-xs text-muted-foreground">Versión {selected.versionKey} · {selected.sourceDate}. Validación técnica pendiente.</p></> : <p role="status" className="mt-3 rounded-lg bg-muted p-3 text-sm font-medium">Configuración y precio orientativo pendientes de revisar. Puedes continuar sin elegir una variante.</p>;
                        })()}
                        <details className="mt-4 text-sm"><summary className="cursor-pointer font-semibold">Ver características</summary><ul className="mt-2 list-disc space-y-1 pl-5"><li>Recarga en corriente alterna con conector Tipo 2.</li><li>Configuraciones monofásica o trifásica según suministro.</li><li>La potencia real la limitan vehículo, suministro e instalación.</li><li>Protecciones, medición, recorrido y alcance se concretan en validación técnica.</li></ul></details>
                      </div>
                    </article>
                    <div className="ev-secondary-options space-y-4">
                      <section className="ev-explainer"><h3 className="font-semibold">Control dinámico</h3><p className="mt-1 text-sm">Interés en ajustar la carga al consumo de la vivienda; requiere medición y revisión eléctrica.</p><label className="mt-3 grid gap-1 text-sm">¿Quieres que lo valoremos?<select aria-label="Interés en control dinámico" className="min-h-11 rounded-lg border bg-background px-3" value={draft.need.evDynamicMeasurement??"unknown"} onChange={e=>setDraft(d=>({...d,need:{...d.need,evDynamicMeasurement:e.target.value}}))}><option value="unknown">Por determinar</option><option value="included">Sí, valorar viabilidad</option><option value="not_included">No por ahora</option></select></label><p className="mt-1 text-xs text-muted-foreground">La solicitud no confirma compatibilidad ni accesorio incluido.</p></section>
                      <section className="ev-explainer"><h3 className="font-semibold">Integración con energía solar</h3><p className="mt-1 text-sm">Si tienes placas, podemos estudiar cómo coordinarlas con la recarga.</p><label className="mt-3 grid gap-1 text-sm">¿Te interesa revisarlo?<select aria-label="Interés en integración solar" className="min-h-11 rounded-lg border bg-background px-3" value={draft.need.evSolarIntegration??"unknown"} onChange={e=>setDraft(d=>({...d,need:{...d.need,evSolarIntegration:e.target.value}}))}><option value="unknown">Por determinar</option><option value="included">Sí, valorar compatibilidad</option><option value="not_included">No por ahora</option></select></label><p className="mt-1 text-xs text-muted-foreground">Compatibilidad y posibles accesorios se confirman después; no se añaden extras ni precios automáticamente.</p></section>
                    </div>
                  </div>
                  <div className="grid gap-4 lg:grid-cols-2">
                    <details className="ev-disclosure"><summary className="cursor-pointer font-semibold">Distancia de instalación</summary><div className="mt-3 grid gap-3"><label className="grid gap-1 text-sm">Distancia aproximada al punto de alimentación<input aria-label="Distancia aproximada al punto de alimentación" className="min-h-11 rounded-lg border bg-background px-3" type="number" min="0" inputMode="numeric" value={draft.need.distanceMeters??""} onChange={e=>setDraft(d=>({...d,need:{...d.need,distanceMeters:e.target.value}}))}/><span className="text-xs text-muted-foreground">metros · estimación visual; el recorrido real se mide en la visita.</span></label></div></details>
                    <details className="ev-disclosure"><summary className="cursor-pointer font-semibold">Preguntas frecuentes</summary><div className="mt-3 space-y-3 text-sm"><p><b>¿Qué determina la potencia?</b><br/>El cargador, el suministro de la vivienda y el límite de carga AC del vehículo. La cifra es máxima teórica, no una garantía.</p><p><b>¿El precio ya está cerrado?</b><br/>No. Es una referencia del catálogo vigente; la oficina valida recorrido, protecciones y trabajos antes de emitir oferta.</p><p><b>¿Las funciones solares o de control están incluidas?</b><br/>No se presuponen. Se estudian compatibilidad y accesorios necesarios, que solo se cotizan por separado si aplican.</p></div></details>
                  </div>
                  <EvEnergyStudyPanel
                    requestId={id ?? null}
                    version={version ?? 1}
                    draft={draft.need.energyStudyDraft ?? {}}
                    legacyAnnualKm={draft.need.evAnnualKm}
                    legacyConsumption={draft.need.evConsumptionKwhPer100Km}
                    documents={docs.data ?? []}
                    onDraftChange={energyStudyDraft => setDraft(current => {
                      const need: Record<string, any> = { ...current.need, energyStudyDraft };
                      if (energyStudyDraft.vehicleAnnualKm) need.evAnnualKm = energyStudyDraft.vehicleAnnualKm.value == null ? "" : String(energyStudyDraft.vehicleAnnualKm.value);
                      if (energyStudyDraft.vehicleConsumptionKwhPer100Km) need.evConsumptionKwhPer100Km = energyStudyDraft.vehicleConsumptionKwhPer100Km.value == null ? "" : String(energyStudyDraft.vehicleConsumptionKwhPer100Km.value);
                      return { ...current, need };
                    })}
                    onPersistDraft={() => persist(step, draftRef.current)}
                  />
                </div>
              )}
              {draft.serviceType === "maintenance" && (
                <div className="space-y-3">
                  <label className="grid gap-1 text-sm">
                    Instalación
                    <select
                      className="h-11 w-full min-w-0 max-w-full rounded-md border bg-background px-3 xl:h-10"
                      value={draft.need.installationOrigin ?? ""}
                      onChange={e =>
                        setDraft(d => ({
                          ...d,
                          need: {
                            ...d.need,
                            installationOrigin: e.target.value,
                          },
                        }))
                      }
                    >
                      <option value="">Elige una opción</option>
                      <option value="known">Conocida por ODS</option>
                      <option value="third_party">De terceros</option>
                      <option value="old">Antigua / sin digitalizar</option>
                      <option value="unknown">No lo sé</option>
                    </select>
                  </label>
                  <Textarea
                    placeholder="Alcance, acceso y medios auxiliares"
                    value={draft.need.scope ?? ""}
                    onChange={e =>
                      setDraft(d => ({
                        ...d,
                        need: { ...d.need, scope: e.target.value },
                      }))
                    }
                  />
                </div>
              )}
              <fieldset
                className="commercial-choice-section space-y-2"
                aria-describedby={sourceError ? "commercial-source-error" : undefined}
                aria-invalid={Boolean(sourceError)}
              >
                <legend className="text-sm font-semibold">
                  ¿Cómo nos has conocido?{sourceSelectionRequired && <span className="commercial-required-badge ml-2">Obligatorio</span>}
                </legend>
                <div role="radiogroup" aria-label="¿Cómo nos has conocido?" className="grid min-w-0 grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5">
                  {([
                    ["direct", "Directo"],
                    ["recommendation", "Recomendación"],
                    ["web", "Web"],
                    ["advertising", "Publicidad"],
                    ["other", "Otro"],
                  ] as const).map(([value, label]) => {
                    const selected = draft.source === value;
                    return <label key={value} className="min-w-0 cursor-pointer">
                      <input
                        ref={element => { sourceRadioRefs.current[value] = element; }}
                        type="radio"
                        name="commercial-source"
                        value={value}
                        checked={selected}
                        required={sourceSelectionRequired}
                        aria-label={label}
                        aria-invalid={Boolean(sourceError)}
                        aria-describedby={sourceError ? "commercial-source-error" : undefined}
                        onChange={() => {
                          setSourceError("");
                          setDraft(current => ({ ...current, source: value }));
                        }}
                        className="peer sr-only"
                      />
                      <span className={`flex min-h-11 w-full min-w-0 items-center justify-center rounded-lg border px-2 py-2 text-center text-sm leading-tight transition-colors peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-ring ${selected ? "border-[#FF4D14] bg-[#FF4D14]/10 font-semibold text-[#17181B]" : "border-border bg-background hover:border-[#FF4D14]/70"}`}>
                        {label}
                      </span>
                    </label>;
                  })}
                </div>
                {sourceError && <p id="commercial-source-error" role="alert" className="text-sm text-destructive">{sourceError}</p>}
                {draft.source === "pending" && !sourceSelectionRequired && <p role="status" className="text-xs text-muted-foreground">Sin indicar en este borrador existente; puedes continuar y completarlo después.</p>}
              </fieldset>
              <Textarea
                required
                aria-required="true"
                placeholder="Describe brevemente la necesidad (necesario para enviar)"
                value={draft.need.description ?? ""}
                onChange={e =>
                  setDraft(d => ({
                    ...d,
                    need: { ...d.need, description: e.target.value },
                  }))
                }
              />
            </div>
          )}
          {activeStage === "details" && (
            <div className="commercial-touch-flow space-y-4">
              <CommercialDocumentManager
                requestId={id ?? 0}
                version={version ?? Number(request.data?.version ?? 1)}
                documents={docs.data ?? []}
                kind="electricity_bill"
                contextualInvoiceFlow
                onManualEntry={() => void goToManualSupplyEntry()}
                onVersion={v => setVersion(v)}
                refresh={async () => {
                  await Promise.all([docs.refetch(), request.refetch()]);
                }}
                readOnly={!id}
              />
              <p className="text-xs text-muted-foreground">
                La factura original permanece privada. Revisa cada candidato y
                su página antes de confirmar o aplicarlo.
              </p>
              {(docs.data ?? []).filter((doc:any)=>doc.extraction?.status==="processing").map((doc:any)=><BillExtractionProgress key={`ocr-progress-${doc.id}`} extraction={doc.extraction}/>)}
              {(docs.data ?? [])
                .filter((doc: any) =>
                  doc.documentKind === "electricity_bill" &&
                  doc.extraction?.status === "failed"
                )
                .map((doc: any) => (
                  <div
                    key={`ocr-failure-${doc.id}`}
                    role="alert"
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm"
                  >
                    <span>
                      No se pudo leer «{doc.fileName}». El original se conserva.
                      {doc.extraction?.errorCode === "OCR_TIMEOUT_RETRY"
                        ? " La lectura tardó demasiado."
                        : doc.extraction?.errorCode === "OCR_BUSY_RETRY"
                          ? " Hay otra lectura en curso."
                          : " Puedes volver a intentarlo o introducir los datos manualmente."}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={retryBillExtraction.isPending}
                      onClick={() =>
                        void retryBillExtraction
                          .mutateAsync({ documentId: Number(doc.id) })
                          .then(() => Promise.all([docs.refetch(), request.refetch()]))
                          .catch(() => setError("No se pudo reintentar la lectura. El original sigue guardado."))
                      }
                    >
                      {retryBillExtraction.isPending ? "Reintentando…" : "Reintentar lectura"}
                    </Button>
                  </div>
                ))}
              <section id="bill-review" className="commercial-data-section space-y-3 scroll-mt-24" aria-label="Facturas y propuestas OCR">
                <h3 className="commercial-data-section-heading">Revisa los datos detectados</h3>
                <p className="text-sm text-muted-foreground">Las propuestas se editan en los campos definitivos del suministro. Revisa su fuente y aplica los datos detectados cuando quieras; los datos ya confirmados se conservan.</p>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" aria-live="polite">
                  {(() => {
                    const canonical = ["holderName", "holderTaxId", "supplyAddress", "postalAddress", "cups", "retailer", "distributor", "tariff", "contractedPowerPeriods", "consumptionPeriods"];
                    const candidates = invoiceDocuments.flatMap((doc: any) => canonical.map(field => doc.extraction.fields[field]).filter(Boolean));
                    const identified = candidates.filter((item: any) => item.value != null && !item.reviewReason).length;
                    const review = candidates.filter((item: any) => item.reviewReason === "multiple_candidates").length;
                    const missing = candidates.filter((item: any) => item.value == null && !item.reviewReason).length;
                    return <><span><strong>{identified}</strong> Datos identificados</span><span><strong>{review}</strong> Necesitan revisión</span><span><strong>{missing}</strong> No encontrados en la factura</span></>;
                  })()}
                  <Button type="button" size="sm" disabled={saving || applyBillBatch.isPending || !invoiceDocuments.length} onClick={() => void applyBillProposalsAndPersist(step)}>Aplicar datos detectados</Button>
                </div>
                {invoiceDocuments.map((doc: any) => {
                  const groupDoc = doc.captureGroupId ? invoiceDocuments.find((sibling: any) => sibling.captureGroupId === doc.captureGroupId && (sibling.extraction?.supplyId || billSupplyAssignments[String(sibling.id)])) : undefined;
                  const fixedSupplyId = doc.extraction?.supplyId ?? groupDoc?.extraction?.supplyId ?? (groupDoc ? billSupplyAssignments[String(groupDoc.id)] : undefined);
                  const assigned = fixedSupplyId ?? billSupplyAssignments[String(doc.id)];
                  const fixedSupplyIndex = draft.parties.supplies?.findIndex((supply: any) => String(supply.id) === String(assigned)) ?? -1;
                  const fieldPriority = ["holderName", "holderTaxId", "supplyAddress", "postalAddress", "cups", "retailer", "distributor", "tariff", "contractedPowerPeriods", "consumptionPeriods"];
                  const fields = Object.entries(doc.extraction.fields).filter(([, candidate]: any) => candidate?.value != null || candidate?.reviewReason).sort(([left], [right]) => {
                    const leftIndex = fieldPriority.indexOf(String(left));
                    const rightIndex = fieldPriority.indexOf(String(right));
                    return (leftIndex < 0 ? fieldPriority.length : leftIndex) - (rightIndex < 0 ? fieldPriority.length : rightIndex);
                  });
                  const isSelected = Number(selectedBillDocument?.id) === Number(doc.id);
                  const reviewBillingDate = async (field: "billingPeriodStart" | "billingPeriodEnd") => {
                    const value = (document.getElementById(`wizard-bill-${doc.id}-${field}`) as HTMLInputElement | null)?.value ?? "";
                    try {
                      await reviewBillField.mutateAsync({ documentId: Number(doc.id), field, value, applyToRequest: false });
                    } catch {
                      setError("No se pudo guardar la revisión de esta fecha. La factura original y sus datos detectados siguen conservados; revisa el valor e inténtalo de nuevo.");
                      return;
                    }
                    try {
                      await docs.refetch();
                    } catch {
                      setError("La fecha se guardó, pero no se pudo actualizar la lectura. Recarga la solicitud para comprobar el estado antes de volver a intentarlo.");
                    }
                  };
                  return <article key={doc.id} className="space-y-2 rounded-xl bg-muted/20 p-3">
                    <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0"><h4 className="font-medium">Factura · {doc.fileName}</h4><p className="text-xs text-muted-foreground">{doc.captureGroupId ? "Grupo multipágina" : "Documento"} · {fields.length} propuestas detectadas</p></div>
                      <Button type="button" size="sm" variant="outline" aria-pressed={isSelected} onClick={() => {
                        if (fixedSupplyIndex >= 0) setActiveSupplyIndex(fixedSupplyIndex);
                        setSelectedBillDocumentId(Number(doc.id));
                        if (!fixedSupplyId && !billSupplyAssignments[String(doc.id)]) {
                          const supplyId = draft.parties.supplies?.[activeSupplyIndex]?.id;
                          if (supplyId) setBillSupplyAssignments(current => ({ ...current, [String(doc.id)]: String(supplyId) }));
                        }
                      }}>{isSelected ? "Propuesta activa en campos finales" : `Editar en Suministro ${fixedSupplyIndex >= 0 ? fixedSupplyIndex + 1 : activeSupplyIndex + 1}`}</Button>
                    </div>
                    <div className="grid gap-3 rounded-lg bg-background/80 p-3 sm:grid-cols-2" aria-label={`Revisión del periodo facturado ${doc.id}`}>
                      <p className="sm:col-span-2 text-sm">Confirma las fechas leídas en la factura para completar el periodo anual. Esto revisa el dato de origen; no lo copia a otro suministro.</p>
                      {(["billingPeriodStart", "billingPeriodEnd"] as const).map((field) => {
                        const candidate = doc.extraction?.fields?.[field] ?? {};
                        const label = field === "billingPeriodStart" ? "Inicio del periodo facturado" : "Fin del periodo facturado";
                        return <div key={field} className="flex min-w-0 flex-wrap items-end gap-2">
                          <label className="grid min-w-0 flex-1 gap-1 text-sm" htmlFor={`wizard-bill-${doc.id}-${field}`}>{label}
                            <Input id={`wizard-bill-${doc.id}-${field}`} type="date" defaultValue={String(candidate.value ?? "")} aria-label={`Revisar ${label}`} />
                          </label>
                          <Button type="button" size="sm" variant={candidate.reviewStatus === "confirmed" ? "secondary" : "outline"} disabled={reviewBillField.isPending} aria-label={`${candidate.reviewStatus === "confirmed" ? "Revisión confirmada" : "Confirmar revisión"} ${label}`} onClick={() => void reviewBillingDate(field)}>{candidate.reviewStatus === "confirmed" ? "Confirmada" : "Confirmar fecha"}</Button>
                          <span className="w-full text-xs text-muted-foreground">{candidate.reviewStatus === "confirmed" ? "Fecha revisada" : "Pendiente de confirmación"}{candidate.sourceText ? ` · leído: ${candidate.sourceText}` : ""}{candidate.provenance ? ` · ${candidate.provenance}` : ""}{candidate.page ? ` · pág. ${candidate.page}` : ""}</span>
                        </div>;
                      })}
                    </div>
                    {(draft.parties.supplies?.length ?? 0) > 1 && !fixedSupplyId && <label className="grid max-w-sm gap-1 text-sm">Suministro de esta factura
                      <select aria-label={`Suministro de factura ${doc.id}`} className="h-11 rounded-md border bg-background px-3" value={assigned ?? ""} onChange={event => {
                        setBillSupplyAssignments(current => ({ ...current, [String(doc.id)]: event.target.value }));
                        setSelectedBillDocumentId(Number(doc.id));
                        setActiveSupplyIndex(draft.parties.supplies.findIndex((supply: any) => String(supply.id) === event.target.value));
                      }}><option value="">Selecciona un suministro</option>{draft.parties.supplies.map((supply: any, index: number) => <option key={supply.id ?? index} value={supply.id ?? ""}>Suministro {index + 1}{supply.cups ? ` · ${supply.cups}` : ""}</option>)}</select>
                    </label>}
                    {isSelected && <p className="text-xs text-muted-foreground">La propuesta seleccionada aparece en los campos técnicos de abajo. Corrige allí si hace falta; origen y página se mantienen en los detalles.</p>}
                    <details className="rounded-lg px-1">
                      <summary className="cursor-pointer text-sm font-medium">Origen, páginas y datos extraídos</summary>
                      <ul className="mt-2 grid gap-2 sm:grid-cols-2">{fields.map(([field, candidate]: any) => <li key={field} className="min-w-0 rounded bg-background/70 p-2 text-xs">
                        <div className="font-medium">{String(field).replace(/([A-Z])/g, " $1")}</div>
                        <BillCandidateEvidence candidate={candidate}/>
                        <div className="text-muted-foreground">{billCandidateSource(candidate)}{candidate.reviewStatus ? ` · ${candidate.reviewStatus}` : " · pendiente de revisión"}{candidate.correctedByHuman ? ` · corregido; original: ${candidate.sourceDocumentValue ?? "conservado en origen"}` : ""}</div>
                        {candidate.reviewReason === "multiple_candidates" && <fieldset className="mt-2 space-y-2" aria-label={`Elige la alternativa para ${String(field).replace(/([A-Z])/g, " $1")}`}><legend className="font-medium">Elige y confirma una alternativa</legend>{(candidate.alternatives ?? []).map((alternative: any, index: number) => <label key={`${alternative.page}:${index}:${alternative.sourceText}`} className="flex items-start gap-2 rounded border p-2"><input type="radio" name={`bill-${doc.id}-${field}`} checked={candidateAlternativeSelections[`${doc.id}:${field}`] === index} onChange={() => { const key = `${doc.id}:${field}`; setCandidateAlternativeSelections(current => ({ ...current, [key]: index })); const value = field === "contractedPowerPeriods" || field === "consumptionPeriods" ? [...(candidate.normalized ?? []).map((row: any) => `${row.period}: ${row.value} ${row.unit}${row.startDate || row.endDate ? ` ${row.startDate ?? ""} - ${row.endDate ?? ""}` : ""}`), `${alternative.period ?? "P1"}: ${alternative.value} ${alternative.unit ?? (field === "contractedPowerPeriods" ? "kW" : "kWh")}${alternative.startDate || alternative.endDate ? ` ${alternative.startDate ?? ""} - ${alternative.endDate ?? ""}` : ""}`].join("\n") : String(alternative.value); setCandidateEdits(current => ({ ...current, [key]: value })); }} /><span>Usar alternativa {index + 1} · {alternative.period ? `${alternative.period}: ` : ""}{alternative.value}{alternative.unit ? ` ${alternative.unit}` : ""}{alternative.startDate || alternative.endDate ? ` · ${alternative.startDate ?? "fecha sin indicar"}–${alternative.endDate ?? "fecha sin indicar"}` : ""} · {alternative.provenance} · pág. {alternative.page}<span className="block text-muted-foreground">{alternative.sourceText}</span></span></label>)}</fieldset>}
                        {Array.isArray(candidate.normalized) && candidate.normalized.length > 0 && <ul className="mt-1 space-y-1">{candidate.normalized.map((reading: any, index: number) => <li key={`${index}-${reading.period}-${reading.value}`}>{reading.period}: {reading.value} {reading.unit ?? ""}{reading.startDate || reading.endDate ? ` · ${reading.startDate ?? "sin inicio"}–${reading.endDate ?? "sin fin"}` : " · intervalo no indicado"}{` · ${reading.source?.provenance ?? "procedencia no indicada"}`}{reading.source?.page ? ` · pág. ${reading.source.page}` : " · página no indicada"}{reading.reviewRequired ? " · revisar periodo" : ""}</li>)}</ul>}
                      </li>)}</ul>
                      {Array.isArray(doc.extraction.fields.consumptionPeriods?.normalized) && <ul aria-label={`Lecturas detectadas de la factura ${doc.id}`} className="mt-2 space-y-1 text-xs">{doc.extraction.fields.consumptionPeriods.normalized.map((reading: any, index: number) => <li key={`${index}-${reading.period}-${reading.value}`}>Consumo {reading.period}: {reading.value} {reading.unit ?? "kWh"}{reading.startDate || reading.endDate ? ` · ${reading.startDate ?? "fecha sin indicar"}–${reading.endDate ?? "fecha sin indicar"}` : " · intervalo no indicado"}{` · ${reading.source?.provenance ?? "procedencia no indicada"}`}{reading.source?.page ? ` · pág. ${reading.source.page}` : " · página no indicada"}{reading.reviewRequired ? ` · ${Array.isArray(reading.reviewReasons) && reading.reviewReasons.includes("period_overlap") ? "solape con otra lectura del mismo periodo; revisar antes de comparar" : "intervalo ambiguo o incompleto; indica fechas antes de comparar"}` : ""}</li>)}</ul>}
                      {(doc.extraction.fields.monthlyConsumption?.facts ?? []).length > 0 && <details className="mt-2"><summary className="cursor-pointer text-sm font-medium">Consumo mensual detectado · {doc.extraction.fields.monthlyConsumption.facts.length}</summary><ul aria-label="Consumo mensual detectado" className="mt-1 grid gap-1 sm:grid-cols-2 text-xs">{(doc.extraction.fields.monthlyConsumption.facts as any[]).map((fact: any, index: number) => <li key={`${fact.period}:${index}`}>{fact.period}: {fact.approximate ? "≈ " : ""}{fact.value} {fact.unit} · {fact.provenance === "pdf_vector_graph" ? "gráfico vectorial · confianza media" : fact.provenance} · pág. {fact.page}{fact.valueRange ? ` · rango estimado ${fact.valueRange.min}–${fact.valueRange.max} ${fact.unit}` : ""}</li>)}</ul>{deriveCompleteMonthlyTotals(doc.extraction.fields.monthlyConsumption.facts).map(total => <p key={total.year} className="mt-2 text-xs font-medium">Suma derivada de 12 meses · {total.year}: {total.approximate ? "≈ " : ""}{total.value} {total.unit} · cálculo informativo, no lectura de factura</p>)}<p className="text-xs text-muted-foreground">Los meses ausentes no se consideran cero ni se convierten automáticamente en lecturas por periodo.</p></details>}
                      {(doc.extraction.fields.invoiceFacts?.facts ?? []).length > 0 && <details className="mt-2"><summary className="cursor-pointer text-sm font-medium">Datos estructurados de factura · {doc.extraction.fields.invoiceFacts.facts.length}</summary><ul className="mt-1 space-y-1 text-xs">{(doc.extraction.fields.invoiceFacts.facts as any[]).map((fact: any, index: number) => <li key={`${fact.key}:${fact.page}:${index}`}><strong>{fact.label}: {String(fact.value)}{fact.unit ? ` ${fact.unit}` : ""}{fact.period ? ` · ${fact.period}` : ""}</strong> · {fact.provenance} · pág. {fact.page} · {fact.sourceText}{fact.applicableField ? ` · propuesta revisable en ${fact.applicableField}` : " · solo informativo"}</li>)}</ul></details>}
                      {(doc.extraction.fields.additionalInformation?.facts ?? []).length > 0 && <details className="mt-2"><summary className="cursor-pointer text-sm font-medium">Otra información legible · {doc.extraction.fields.additionalInformation.facts.length}</summary><ul className="mt-1 space-y-1 text-xs">{(doc.extraction.fields.additionalInformation.facts as any[]).map((fact: any, index: number) => <li key={`additional:${fact.page}:${index}`}>{fact.label.replace(/_/g, " ")} · pág. {fact.page} · {fact.provenance}: {fact.value}</li>)}</ul></details>}
                    </details>
                  </article>;
                })}
                {!(docs.data ?? []).some((doc: any) => doc.documentKind === "electricity_bill") && <p className="text-sm text-muted-foreground">Sin factura adjunta. Puedes introducir los datos manualmente.</p>}
                {(docs.data ?? []).some((doc: any) => doc.documentKind === "electricity_bill" && !doc.extraction?.fields) && <p role="status" className="text-sm">Hay una factura en proceso o sin candidatos; el original se conserva y puedes reintentar su lectura.</p>}
              </section>
              <section className="commercial-data-section space-y-3" aria-labelledby="commercial-client-section-heading">
              <h3 id="commercial-client-section-heading" className="commercial-data-section-heading">A · Cliente y contratante</h3>
              <label className="block text-sm">
                Buscar en mis clientes
                <Input controlSize="touch"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Nombre, dirección o teléfono"
                />
              </label>
              {customerOptions.map((c: any) => (
                <button
                  key={c.id}
                  className={`mr-2 rounded-lg border p-2 text-left text-sm ${draft.customerPartnerId === Number(c.id) ? "border-primary bg-primary/5" : ""}`}
                  onClick={() => changeCanonicalCustomer(c)}
                >
                  {c.tradeName || c.legalName} · {c.lifecycleStage}
                </button>
              ))}
              {!draft.customerPartnerId && (
                <p className="text-xs text-muted-foreground">
                  También puedes completar el cliente manualmente como prospecto
                  en los campos.
                </p>
              )}
              {draft.customerPartnerId && (
                <section aria-label="Sedes y contactos del cliente seleccionado" className="min-w-0 space-y-3 rounded-xl border p-3">
                  <h3 className="font-medium">Ficha canónica seleccionada</h3>
                  <label className="grid min-w-0 gap-1 text-sm">Sede existente
                    <select aria-label="Sede existente" className="h-11 w-full min-w-0 max-w-full rounded-md border bg-background px-3 xl:h-10" value={draft.siteId ?? ""} onChange={e => setDraft(d => ({ ...d, siteId: e.target.value ? Number(e.target.value) : null }))}>
                      <option value="">Sin sede vinculada</option>
                      {(context.data?.sites ?? []).filter((site: any) => Number(site.businessPartnerId) === draft.customerPartnerId).map((site: any) => (
                        <option key={site.id} value={site.id}>{site.name} · {[site.addressLine1, site.postalCode, site.city].filter(Boolean).join(", ")}</option>
                      ))}
                    </select>
                  </label>
                  <details><summary className="cursor-pointer text-sm font-medium">Crear sede nueva para este cliente</summary>
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      <Input controlSize="touch" aria-label="Nombre de la nueva sede" placeholder="Nombre de la sede" value={siteDraft.name} onChange={e => setSiteDraft(v => ({ ...v, name: e.target.value }))}/>
                      <Input controlSize="touch" aria-label="Dirección de la nueva sede" placeholder="Dirección" value={siteDraft.addressLine1} onChange={e => setSiteDraft(v => ({ ...v, addressLine1: e.target.value }))}/>
                      <Input controlSize="touch" aria-label="Código postal de la nueva sede" placeholder="Código postal" value={siteDraft.postalCode} onChange={e => setSiteDraft(v => ({ ...v, postalCode: e.target.value }))}/>
                      <Input controlSize="touch" aria-label="Ciudad de la nueva sede" placeholder="Ciudad" value={siteDraft.city} onChange={e => setSiteDraft(v => ({ ...v, city: e.target.value }))}/>
                      <Button type="button" onClick={() => void createCanonicalSite()} disabled={createCustomerSite.isPending || !siteDraft.name.trim() || !siteDraft.addressLine1.trim()}>{createCustomerSite.isPending ? "Creando sede…" : "Crear y vincular sede"}</Button>
                    </div>
                  </details>
                  <div className="min-w-0 space-y-2">
                    <h4 className="text-sm font-medium">Contactos canónicos del cliente</h4>
                    {(context.data?.contacts ?? []).filter((contact: any) => Number(contact.businessPartnerId) === draft.customerPartnerId).map((contact: any) => (
                      <div key={contact.id} className="flex min-w-0 flex-col gap-2 rounded border p-2 text-sm sm:flex-row sm:items-center sm:justify-between">
                        <span className="min-w-0 [overflow-wrap:anywhere] sm:flex-1">{contact.displayName}{contact.jobTitle ? ` · ${contact.jobTitle}` : ""}{contact.primary ? " · principal" : ""}</span>
                        <div className="flex min-w-0 flex-wrap gap-2 sm:shrink-0"><Button type="button" size="sm" variant="outline" onClick={() => addExistingCanonicalContact(contact)} disabled={(draft.parties.contacts ?? []).some((item: any) => Number(item.contactId) === Number(contact.id))}>Añadir</Button><Button type="button" size="sm" variant="outline" onClick={() => addExistingCanonicalContact(contact, true)} disabled={(draft.parties.contacts ?? []).some((item: any) => Number(item.contactId) === Number(contact.id) && item.primary)}>Usar como principal</Button></div>
                      </div>
                    ))}
                    {(draft.parties.contacts ?? []).some((item: any) => {
                      const canonical = (context.data?.contacts ?? []).find((candidate: any) => Number(candidate.id) === Number(item.contactId));
                      const linkedSiteId = item.siteId ?? canonical?.siteId ?? null;
                      return linkedSiteId != null && Number(linkedSiteId) !== Number(draft.siteId);
                    }) && <p role="alert" className="text-sm">Hay contactos vinculados a otra sede. Retíralos del borrador o vuelve a seleccionar su sede antes de guardar.</p>}
                    {(draft.parties.contacts ?? []).map((contact: any, index: number) => (
                      <div key={contact.contactId ?? contact.clientKey ?? index} className="flex min-w-0 flex-col gap-2 rounded bg-muted px-2 py-1 text-xs sm:flex-row sm:items-center sm:justify-between">
                        <span className="min-w-0 [overflow-wrap:anywhere] sm:flex-1">{contact.name} · {contact.role || "Sin función"} · {contact.phone || contact.email || "Sin canal"}{contact.primary ? " · principal" : ""}</span>
                        <div className="flex min-w-0 flex-wrap gap-2 sm:shrink-0"><Button type="button" size="sm" variant="outline" disabled={Boolean(contact.primary) || !contact.contactId} onClick={() => setDraft(current => ({ ...current, contactId: Number(contact.contactId), parties: { ...current.parties, contactName: contact.name, ...(contact.phone ? { phone: contact.phone, contactMethod: contact.phone } : {}), ...(contact.email ? { email: contact.email, contactMethod: contact.email } : {}), contacts: (current.parties.contacts ?? []).map((item: any) => ({ ...item, primary: Number(item.contactId) === Number(contact.contactId) })) } }))}>Usar como principal</Button><Button type="button" size="sm" variant="outline" aria-label={`Retirar contacto ${contact.name}`} onClick={() => removeCanonicalContact(contact)}>Retirar</Button></div>
                      </div>
                    ))}
                  </div>
                </section>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="grid gap-1 text-sm">Cliente / razón social<Input controlSize="touch" aria-label="Cliente o razón social" placeholder="Cliente / razón social" value={draft.parties.clientName ?? ""} onChange={e => setDraft(d => ({ ...d, parties: { ...d.parties, clientName: e.target.value } }))}/></label>
                <label className="grid gap-1 text-sm">Tipo de cliente <span className="commercial-required-badge">Obligatorio</span>
                  <select ref={partyTypeInputRef} aria-label="Tipo de cliente" aria-invalid={Boolean(partyTypeError)} aria-describedby={partyTypeError ? "commercial-party-type-error" : undefined} className="h-11 rounded-md border bg-background px-3" value={draft.parties.partyType ?? ""} onChange={e => { setPartyTypeError(""); setDraft(d => ({ ...d, parties: { ...d.parties, partyType: e.target.value || undefined } })); }}>
                    <option value="">Por indicar</option><option value="person">Persona</option><option value="organization">Organización</option>
                  </select>
                  {partyTypeError && <span id="commercial-party-type-error" role="alert" className="text-sm">{partyTypeError}</span>}
                </label>
                <label className="grid gap-1 text-sm">NIF del cliente · si se conoce<Input controlSize="touch" aria-label="NIF del cliente" value={draft.parties.taxId ?? ""} onChange={e => setDraft(d => ({ ...d, parties: { ...d.parties, taxId: e.target.value } }))}/></label>
                <label className="grid gap-1 text-sm">Contratante previsto<Input controlSize="touch" aria-label="Contratante previsto" placeholder="Contratante previsto" value={draft.parties.contractingPartyName ?? ""} onChange={e => setDraft(d => ({ ...d, parties: { ...d.parties, contractingPartyName: e.target.value } }))}/></label>
                <label className="grid gap-1 text-sm">Persona de contacto<Input controlSize="touch" aria-label="Persona de contacto" placeholder="Persona de contacto" value={draft.parties.contactName ?? ""} onChange={e => setDraft(d => ({ ...d, parties: { ...d.parties, contactName: e.target.value } }))}/></label>
              </div>
              </section>
              <section className="commercial-data-section space-y-3" aria-labelledby="commercial-contact-section-heading">
                <h3 id="commercial-contact-section-heading" className="commercial-data-section-heading">B · Datos de contacto</h3>
                <p className="text-sm text-muted-foreground">{isPreviouslySubmittedResponse
                  ? "Solicitud enviada previamente: conserva al menos un canal de contacto válido para responder y reenviar."
                  : "Necesitamos ambos datos para enviar la solicitud. Si aún no los tienes, sal de forma segura y vuelve para completarlos: el borrador se conserva automáticamente."}</p>
                <div className="grid gap-3 sm:grid-cols-2">
                <label htmlFor="commercial-contact-phone" className="grid min-w-0 gap-1.5 text-sm font-medium">
                  <span>Teléfono {!isPreviouslySubmittedResponse && <span className="commercial-required-badge">Obligatorio</span>}</span>
                  <Input
                    ref={phoneInputRef}
                    id="commercial-contact-phone"
                    controlSize="touch"
                    type="tel"
                    value={draft.parties.phone ?? ""}
                    aria-invalid={phoneError ? "true" : undefined}
                    aria-describedby={phoneError ? "commercial-contact-phone-error" : "commercial-contact-phone-pending"}
                    onChange={e => {
                      const value = e.target.value;
                      setDraft(d => ({ ...d, parties: { ...d.parties, phone: value, contactMethod: value || d.parties.email || "" } }));
                      if (isPreviouslySubmittedResponse) {
                        const updated = { ...draftRef.current.parties, phone: value, contactMethod: value || draftRef.current.parties.email || "" };
                        if (hasCommercialSubmissionContactMethod(updated)) { setPhoneError(""); setEmailError(""); setContactGateMessage(""); }
                        else setPhoneError(value && !isValidCommercialPhone(value) ? "Introduce un teléfono válido." : "");
                      } else if (contactValidationAttempted) setPhoneError(!value.trim() ? "El teléfono es obligatorio." : isValidCommercialPhone(value) ? "" : "Introduce un teléfono válido.");
                    }}
                    onBlur={e => {
                      const value = e.currentTarget.value.trim();
                      if (value !== e.currentTarget.value) setDraft(d => ({ ...d, parties: { ...d.parties, phone: value, contactMethod: value || d.parties.email || "" } }));
                      if (contactValidationAttempted && value && !isValidCommercialPhone(value)) setPhoneError("Introduce un teléfono válido.");
                    }}
                  />
                  {phoneError ? <span id="commercial-contact-phone-error" className="font-normal text-destructive">{phoneError}</span> : <span id="commercial-contact-phone-pending" role="status" className="font-normal text-ods-navigation">Pendiente de solicitar al cliente</span>}
                </label>
                <label htmlFor="commercial-contact-email" className="grid min-w-0 gap-1.5 text-sm font-medium">
                  <span>Email {!isPreviouslySubmittedResponse && <span className="commercial-required-badge">Obligatorio</span>}</span>
                  <Input
                    ref={emailInputRef}
                    id="commercial-contact-email"
                    controlSize="touch"
                    placeholder="Email"
                    type="email"
                    value={draft.parties.email ?? ""}
                    aria-invalid={emailError ? "true" : undefined}
                    aria-describedby={emailError ? "commercial-contact-email-error" : "commercial-contact-email-pending"}
                    onChange={e => {
                      const value = e.target.value;
                      setDraft(d => ({ ...d, parties: { ...d.parties, email: value, contactMethod: d.parties.phone || value } }));
                      if (isPreviouslySubmittedResponse) {
                        const updated = { ...draftRef.current.parties, email: value, contactMethod: draftRef.current.parties.phone || value };
                        if (hasCommercialSubmissionContactMethod(updated)) { setPhoneError(""); setEmailError(""); setContactGateMessage(""); }
                        else setEmailError(value && !isValidCommercialEmail(value) ? "Introduce un email válido." : "");
                      } else if (contactValidationAttempted) setEmailError(!value.trim() ? "El email es obligatorio." : isValidCommercialEmail(value) ? "" : "Introduce un email válido.");
                    }}
                    onBlur={e => {
                      const value = e.currentTarget.value.trim();
                      if (value !== e.currentTarget.value) setDraft(d => ({ ...d, parties: { ...d.parties, email: value, contactMethod: d.parties.phone || value } }));
                      if (contactValidationAttempted && value && !isValidCommercialEmail(value)) setEmailError("Introduce un email válido.");
                    }}
                  />
                  {emailError ? <span id="commercial-contact-email-error" className="font-normal text-destructive">{emailError}</span> : <span id="commercial-contact-email-pending" role="status" className="font-normal text-ods-navigation">Pendiente de solicitar al cliente</span>}
                </label>

                </div>
                <details><summary className="cursor-pointer font-medium">Añadir contacto adicional</summary>
                  {draft.customerPartnerId ? <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <Input controlSize="touch" aria-label="Nombre del nuevo contacto" placeholder="Nombre del contacto" value={contactDraft.name} onChange={e => setContactDraft(v => ({ ...v, name: e.target.value }))}/>
                    <Input controlSize="touch" aria-label="Relación o función del contacto" placeholder="Relación o función" value={contactDraft.role} onChange={e => setContactDraft(v => ({ ...v, role: e.target.value }))}/>
                    <Input controlSize="touch" aria-label="Teléfono del nuevo contacto" placeholder="Teléfono" value={contactDraft.phone} onChange={e => setContactDraft(v => ({ ...v, phone: e.target.value }))}/>
                    <Input controlSize="touch" aria-label="Email del nuevo contacto" placeholder="Email" type="email" value={contactDraft.email} onChange={e => setContactDraft(v => ({ ...v, email: e.target.value }))}/>
                    <label className="flex items-center gap-2 text-sm"><input aria-label="Contacto principal" type="checkbox" checked={contactDraft.primary} onChange={e => setContactDraft(v => ({ ...v, primary: e.target.checked }))}/>Contacto principal</label>
                    <Button type="button" onClick={() => void createCanonicalContact()} disabled={createCustomerContact.isPending || !contactDraft.name.trim()}>{createCustomerContact.isPending ? "Creando contacto…" : "Crear contacto canónico"}</Button>
                  </div> : <p className="mt-3 text-xs text-muted-foreground">Selecciona un cliente canónico para crear y vincular contactos; los prospectos manuales siguen usando los campos libres.</p>}
                  <div className="mt-3 grid gap-3 sm:grid-cols-2"><Input controlSize="touch" placeholder="Nombre y función" aria-label="Nombre y función del contacto adicional" value={draft.parties.extraContactName ?? ""} onChange={e => setDraft(d => ({ ...d, parties: { ...d.parties, extraContactName: e.target.value } }))}/><Input controlSize="touch" placeholder="Teléfono o email" aria-label="Teléfono o email del contacto adicional" value={draft.parties.extraContactMethod ?? ""} onChange={e => setDraft(d => ({ ...d, parties: { ...d.parties, extraContactMethod: e.target.value } }))}/></div>
                </details>
              </section>
              <section className="commercial-data-section space-y-3" aria-labelledby="commercial-supply-section-heading">
                <h3 id="commercial-supply-section-heading" className="commercial-data-section-heading">C · Datos técnicos del suministro</h3>
              <details ref={manualSupplyRef} open id="manual-supply-data" className="rounded-2xl bg-muted/25 p-4">
                <summary className="cursor-pointer font-semibold">
                  Datos técnicos eléctricos · por suministro
                </summary>
                <div className="mt-3 space-y-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const next = (draft.parties.supplies ?? []).length;
                        setDraft(d => ({
                          ...d,
                          parties: {
                            ...d.parties,
                            supplies: [
                              ...(d.parties.supplies ?? []),
                              { id: crypto.randomUUID() },
                            ],
                          },
                        }));
                        setActiveSupplyIndex(next);
                      }}
                    >
                      Añadir suministro
                    </Button>
                    {(draft.parties.supplies ?? []).map(
                      (_: any, index: number) => (
                        <button
                          type="button"
                          key={index}
                          aria-pressed={activeSupplyIndex === index}
                          onClick={() => setActiveSupplyIndex(index)}
                          className={`rounded-lg border px-3 py-2 text-sm ${activeSupplyIndex === index ? "border-primary bg-primary/5" : ""}`}
                        >
                          Suministro {index + 1}
                        </button>
                      )
                    )}
                  </div>
                  <label className="grid max-w-2xl gap-1 text-sm">Dirección postal / de facturación
                    <Input controlSize="touch" aria-label="Dirección postal de facturación" disabled={applyBillBatch.isPending} value={String(draft.location.billAddress ?? "").trim() ? String(draft.location.billAddress) : (billCandidate("postalAddress")?.value ?? "")} onChange={event => {
                      const proposal = billCandidate("postalAddress");
                      const hasPendingBillBatch = billBatchKeys.current.size > 0;
                      if (!String(draft.location.billAddress ?? "").trim() && proposal && !hasPendingBillBatch) setCandidateEdits(current => ({ ...current, [proposal.key]: event.target.value }));
                      else setDraft(current => ({ ...current, location: { ...current.location, billAddress: event.target.value } }));
                    }} />
                    {billSourceNote("postalAddress")}
                  </label>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="grid gap-1 text-sm">
                      Titular del suministro
                      <Input controlSize="touch" aria-label="Titular del suministro" disabled={applyBillBatch.isPending} value={finalSupplyFieldValue("holderName", "supplyHolderName")} onChange={e => updateFinalSupplyField("holderName", "supplyHolderName", e.target.value)} />
                      {billSourceNote("holderName")}
                    </label>
                    <label className="grid gap-1 text-sm">
                      NIF del titular
                      <Input controlSize="touch" aria-label="NIF del titular" disabled={applyBillBatch.isPending} value={finalSupplyFieldValue("holderTaxId", "supplyHolderTaxId")} onChange={e => updateFinalSupplyField("holderTaxId", "supplyHolderTaxId", e.target.value)} />
                      {billSourceNote("holderTaxId")}
                    </label>
                    <label className="grid gap-1 text-sm">
                      Dirección del suministro
                      <Input controlSize="touch" aria-label="Dirección del suministro" disabled={applyBillBatch.isPending} value={finalSupplyFieldValue("address", "supplyAddress", "supplyAddress")} onChange={e => updateFinalSupplyField("address", "supplyAddress", e.target.value, "supplyAddress")} />
                      {billSourceNote("supplyAddress")}
                    </label>
                    <label className="grid gap-1 text-sm">
                      CUPS
                      <Input controlSize="touch" aria-label="CUPS del suministro" disabled={applyBillBatch.isPending} value={finalSupplyFieldValue("cups", "supplyCups")} onChange={e => updateFinalSupplyField("cups", "supplyCups", e.target.value)} />
                      {billSourceNote("cups")}
                    </label>
                    <label className="grid gap-1 text-sm">
                      Comercializadora
                      <Input controlSize="touch" aria-label="Comercializadora" disabled={applyBillBatch.isPending} value={finalSupplyFieldValue("retailer", "supplyRetailer")} onChange={e => updateFinalSupplyField("retailer", "supplyRetailer", e.target.value)} />
                      {billSourceNote("retailer")}
                    </label>
                    <label className="grid gap-1 text-sm">
                      Distribuidora (separada)
                      <Input controlSize="touch" aria-label="Distribuidora" disabled={applyBillBatch.isPending} value={finalSupplyFieldValue("distributor", "supplyDistributor")} onChange={e => updateFinalSupplyField("distributor", "supplyDistributor", e.target.value)} />
                      {billSourceNote("distributor")}
                    </label>
                    <label className="grid gap-1 text-sm">
                      Tarifa
                      <select
                        aria-label="Tarifa del suministro"
                        disabled={applyBillBatch.isPending}
                        className="h-11 w-full min-w-0 max-w-full rounded-md border bg-background px-3 xl:h-10"
                        value={finalSupplyFieldValue("tariff", "supplyTariff")}
                        onChange={e => updateFinalSupplyField("tariff", "supplyTariff", e.target.value)}
                      >
                        {[
                          "unknown",
                          "2.0TD",
                          "3.0TD",
                          "6.1TD",
                          "6.2TD",
                          "6.3TD",
                          "6.4TD",
                          "other",
                        ].map(t => (
                          <option key={t} value={t}>
                            {t === "unknown"
                              ? "No lo sé"
                              : t === "other"
                                ? "Otra tarifa"
                                : t}
                          </option>
                        ))}
                      </select>
                      {billSourceNote("tariff")}
                    </label>
                    <label className="grid gap-1 text-sm">
                      Fase
                      <select
                        className="h-11 w-full min-w-0 max-w-full rounded-md border bg-background px-3 xl:h-10"
                        value={primarySupply.phase ?? "unknown"}
                        onChange={e =>
                          updatePrimarySupply({ phase: e.target.value })
                        }
                      >
                        <option value="unknown">No lo sé</option>
                        <option value="mono">Monofásica</option>
                        <option value="tri">Trifásica</option>
                      </select>
                    </label>
                    <label className="grid gap-1 text-sm">
                      Tensión de conexión
                      <select
                        className="h-11 w-full min-w-0 max-w-full rounded-md border bg-background px-3 xl:h-10"
                        value={primarySupply.connectionVoltage ?? "unknown"}
                        onChange={e =>
                          updatePrimarySupply({
                            connectionVoltage: e.target.value,
                          })
                        }
                      >
                        <option value="unknown">No lo sé</option>
                        <option value="230V">230 V</option>
                        <option value="400V">400 V</option>
                        <option value="other">Otra</option>
                      </select>
                    </label>
                  </div>
                  <fieldset className="rounded-lg border p-3">
                    <legend className="px-1 text-sm font-medium">
                      Potencia contratada · kW
                    </legend>
                    <div className="grid gap-2 sm:grid-cols-3">
                      {(primarySupply.tariff === "2.0TD"
                        ? ["P1", "P2"]
                        : ["P1", "P2", "P3", "P4", "P5", "P6"]
                      ).map((period: string) => (
                        <label key={period} className="text-sm">
                          {period}
                          <Input controlSize="touch"
                            type="number"
                            min="0"
                            step="0.01"
                            aria-label={`Potencia ${period} kW`}
                            disabled={applyBillBatch.isPending}
                            value={finalReadingValue("contractedPowerPeriods", period)}
                            onChange={e => updateFinalReading("contractedPowerPeriods", period, e.target.value)}
                          />
                        </label>
                      ))}
                    </div>
                    {billSourceNote("contractedPowerPeriods")}
                    <p className="text-xs text-muted-foreground">
                      {primarySupply.tariff === "2.0TD"
                        ? "2.0TD: P1–P2 de potencia."
                        : "3.0TD y 6.xTD: P1–P6. Si la tarifa es desconocida se muestran seis periodos."}{" "}
                      Los valores previos de otras tarifas se conservan como
                      lectura histórica.
                    </p>
                  </fieldset>
                  <fieldset className="rounded-lg border p-3">
                    <legend className="px-1 text-sm font-medium">
                      Consumo de energía · kWh por periodo y periodo de factura
                    </legend>
                    <div className="grid gap-2 sm:grid-cols-3">
                      {(primarySupply.tariff === "2.0TD"
                        ? ["P1", "P2", "P3"]
                        : ["P1", "P2", "P3", "P4", "P5", "P6"]
                      ).map((period: string) => (
                        <label key={period} className="text-sm">
                          {period}
                          <Input controlSize="touch"
                            type="number"
                            min="0"
                            step="0.01"
                            aria-label={`Consumo ${period} kWh`}
                            disabled={applyBillBatch.isPending}
                            value={finalReadingValue("consumptionPeriods", period)}
                            onChange={e => updateFinalReading("consumptionPeriods", period, e.target.value)}
                          />
                        </label>
                      ))}
                    </div>
                    {billSourceNote("consumptionPeriods")}
                    <div className="my-2 grid gap-2 sm:grid-cols-2">
                      <label className="grid gap-1 text-xs">
                        Inicio de factura para nuevas lecturas
                        <Input controlSize="touch"
                          aria-label="Inicio del periodo de consumo"
                          type="date"
                          value={manualPeriodStart}
                          onChange={e => setManualPeriodStart(e.target.value)}
                        />
                      </label>
                      <label className="grid gap-1 text-xs">
                        Fin de factura para nuevas lecturas
                        <Input controlSize="touch"
                          aria-label="Fin del periodo de consumo"
                          type="date"
                          value={manualPeriodEnd}
                          onChange={e => setManualPeriodEnd(e.target.value)}
                        />
                      </label>
                    </div>
                    <p className="mb-2 text-xs text-muted-foreground">
                      El intervalo elegido se aplica a las próximas lecturas
                      manuales; el historial y las facturas previas no se
                      reescriben ni se suman automáticamente.
                    </p>
                    <ul className="mb-3 space-y-1 text-xs">
                      {(primarySupply.consumptionPeriods ?? []).map(
                        (reading: any, index: number) => (
                          <li
                            key={`${index}-${reading.period}-${reading.value}`}
                          >
                            {reading.period}: {reading.value} kWh
                            {reading.startDate || reading.endDate
                              ? ` · ${reading.startDate ?? "fecha sin indicar"}–${reading.endDate ?? "fecha sin indicar"}`
                              : " · intervalo no indicado"}
                            {reading.source?.page
                              ? ` · pág. ${reading.source.page}`
                              : ""}{" "}
                            · {reading.source?.provenance ?? "sin procedencia"}
                            {reading.reviewRequired
                              ? ` · ${Array.isArray(reading.reviewReasons) && reading.reviewReasons.includes("period_overlap")
                                  ? "solape con otra lectura del mismo periodo; revisar antes de comparar"
                                  : "intervalo ambiguo o incompleto; indica fechas antes de comparar"}`
                              : ""}
                          </li>
                        )
                      )}
                    </ul>
                    <p className="text-xs text-muted-foreground">
                      No se suman lecturas de páginas repetidas; cada periodo y
                      sus fechas quedan vinculados al suministro.
                    </p>
                  </fieldset>
                  <details>
                    <summary className="cursor-pointer text-sm">
                      Valores anteriores sin pérdida
                    </summary>
                    <p className="mt-2 text-xs">
                      Potencia anterior: {draft.parties.power || "—"} · Consumo
                      anterior: {draft.parties.consumption || "—"} · Tarifa
                      anterior: {draft.parties.supplyTariff || "—"}
                    </p>
                  </details>
                </div>
              </details>
              </section>
              <section className="commercial-data-section space-y-3" aria-labelledby="commercial-consumption-section-heading">
                <h3 id="commercial-consumption-section-heading" className="commercial-data-section-heading">D · Consumos y necesidades futuras</h3>
              <details>
                <summary className="cursor-pointer font-medium">
                  Pauta de uso y consumo
                </summary>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  {patternDimensions.map(([key, label, options]) => (
                    <label key={key} className="grid gap-1 text-sm">
                      {label}
                      <select
                        className="h-11 w-full min-w-0 max-w-full rounded-md border bg-background px-3 xl:h-10"
                        value={
                          draft.need.consumptionPattern?.[key] ?? "unknown"
                        }
                        onChange={e =>
                          setDraft(d => ({
                            ...d,
                            need: {
                              ...d.need,
                              consumptionPattern: {
                                ...(typeof d.need.consumptionPattern ===
                                "object"
                                  ? d.need.consumptionPattern
                                  : {}),
                                [key]: e.target.value,
                              },
                            },
                          }))
                        }
                      >
                        {options.map(([value, text]) => (
                          <option key={value} value={value}>
                            {text}
                          </option>
                        ))}
                      </select>
                    </label>
                  ))}
                </div>
              </details>
              <details>
                <summary className="cursor-pointer font-medium">
                  Necesidades futuras
                </summary>
                <p className="mt-2 text-sm text-muted-foreground">Indica cada previsión por separado; una descripción libre no se interpreta como confirmación.</p>
                <div className="mt-3 grid gap-3 sm:grid-cols-3">
                  {(Object.entries(FUTURE_NEED_LABELS) as Array<[keyof typeof FUTURE_NEED_LABELS, string]>).map(([key, label]) => (
                    <label key={key} className="grid gap-1 text-sm">
                      {label}
                      <select aria-label={`Necesidad futura: ${label}`} className="h-11 w-full min-w-0 max-w-full rounded-md border bg-background px-3 xl:h-10" value={draft.need.futureNeeds?.[key] ?? "not_indicated"} onChange={e => setDraft(d => ({ ...d, need: { ...d.need, futureNeeds: { ...(d.need.futureNeeds && typeof d.need.futureNeeds === "object" ? d.need.futureNeeds : {}), [key]: e.target.value as "not_indicated" | "not_applicable" | "planned" } } }))}>
                        <option value="not_indicated">No indicado</option>
                        <option value="not_applicable">No aplica</option>
                        <option value="planned">Previsto</option>
                      </select>
                    </label>
                  ))}
                </div>
              </details>
              </section>
            </div>
          )}
          {activeStage === "location" && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">{draft.serviceType === "photovoltaic" ? "Ubicación y cubierta" : "Ubicación"}</h2>
              <p className="text-sm">{draft.serviceType === "photovoltaic" ? "Indica la dirección del suministro, ajusta el pin y, si procede, dibuja las cubiertas. Se guardará al continuar." : "Indica dónde se realizará el trabajo; la ubicación se confirmará al continuar."}</p>
              <div className="flex gap-2">
                <Input controlSize="touch"
                  placeholder="Dirección de trabajo"
                  value={draft.location.address ?? ""}
                  disabled={confirmingLocation}
                  role="combobox"
                  aria-autocomplete="list"
                  aria-expanded={Boolean(
                    debouncedAddress === currentAddress &&
                    selectedSuggestion !== currentAddress &&
                    (autocomplete.data?.length || autocomplete.isError)
                  )}
                  aria-controls={autocomplete.data?.length ? "commercial-address-suggestions" : undefined}
                  onChange={e => {
                    if (confirmLocationInFlight.current) return;
                    geocodeRequest.current += 1;
                    setAddressSearchPending(false);
                    setChoices([]);
                    setResolvedCartoCandidate(null);
                    setMapError("");
                    setPin(null);
                    setLocationSource("manual");
                    setSelectedSuggestion("");
                    setDraft(d => ({
                      ...d,
                      location: { ...d.location, address: e.target.value, installationAddress: e.target.value, ...(e.target.value === d.location.address ? {} : { addressDetails: undefined }), pending: true },
                    }));
                  }}
                />
                <Button
                  size="touch"
                  variant="outline"
                  onClick={() => void searchAddress()}
                  disabled={geocode.isPending || addressSearchPending || confirmingLocation}
                >
                  Buscar dirección
                </Button>
              </div>
              {postalLocationProposal.requiresPostalDecision && !String(draft.location.address ?? "").trim() && <fieldset className="grid max-w-2xl gap-2 text-sm">
                <legend>¿La instalación se realizará en esta dirección?</legend>
                <p className="text-muted-foreground">{postalLocationProposal.postalAddress}</p>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="touch" variant={postalAsInstallation === true ? "default" : "outline"} onClick={() => { geocodeRequest.current += 1; setAddressSearchPending(false); setChoices([]); setResolvedCartoCandidate(null); setMapError(""); setSelectedSuggestion(""); setPostalAsInstallation(true); setPin(null); setLocationSource("manual"); setDraft(d => ({ ...d, location: { ...d.location, address: postalLocationProposal.postalAddress, installationAddress: postalLocationProposal.postalAddress, addressDetails: undefined, pending: true } })); startAutoResolveForAddress(postalLocationProposal.postalAddress); }}>Sí, es el emplazamiento</Button>
                  <Button type="button" size="touch" variant={postalAsInstallation === false ? "default" : "outline"} onClick={() => { geocodeRequest.current += 1; setAddressSearchPending(false); setChoices([]); setResolvedCartoCandidate(null); setMapError(""); setSelectedSuggestion(""); setPostalAsInstallation(false); setPin(null); setDraft(d => ({ ...d, location: { ...d.location, address: "", installationAddress: "", addressDetails: undefined, pending: true } })); }}>No, indicaré otra</Button>
                </div>
              </fieldset>}
              {debouncedAddress === currentAddress && currentAddress.length >= 3 && selectedSuggestion !== currentAddress && (
                autocomplete.isError ? (
                  <p role="status" className="text-sm text-amber-800">
                    {String((autocomplete.error as any)?.message ?? "").includes("MAPS_CONFIGURATION")
                      ? "Google Maps no está configurado. Pulsa Buscar para consultar CartoCiudad/CNIG; también puedes ajustar el pin sobre PNOA o dejar la ubicación pendiente."
                      : /MAPS_(TIMEOUT|UPSTREAM_HTTP|NETWORK|INVALID_RESPONSE)/.test(String((autocomplete.error as any)?.message ?? ""))
                        ? "Google Maps no respondió correctamente. Pulsa Buscar para intentar CartoCiudad/CNIG o reintenta más tarde; también puedes ajustar el pin o dejar la ubicación pendiente."
                        : "No hay sugerencias de Google Maps. Pulsa Buscar para consultar CartoCiudad/CNIG o ajusta el pin sobre PNOA."}
                  </p>
                ) : autocomplete.data?.length ? (
                  <ul id="commercial-address-suggestions" role="listbox" aria-label="Sugerencias de dirección" className="max-h-56 overflow-y-auto rounded-lg border">
                    {autocomplete.data.map((suggestion: any) => (
                      <li key={suggestion.placeId} role="presentation">
                        <button
                          type="button"
                          role="option"
                          aria-selected="false"
                          className="block w-full whitespace-normal p-3 text-left text-sm hover:bg-slate-50 focus-visible:outline focus-visible:outline-2"
                          disabled={confirmingLocation}
                          onClick={() => {
                            if (confirmLocationInFlight.current) return;
                            geocodeRequest.current += 1;
                            setAddressSearchPending(false);
                            setChoices([]);
                            setResolvedCartoCandidate(null);
                            setMapError("");
                            setPin(null);
                            setLocationSource("google_geocoder");
                            setSelectedSuggestion(suggestion.description);
                            setAddressSearchPending(true);
                            void geocode.mutateAsync({ placeId: suggestion.placeId }).then(results => {
                              const match = results.find((result: any) => result.placeId === suggestion.placeId);
                              if (!match) throw new Error("MAPS_PLACE_ID_MISMATCH");
                              setPin({ latitude: match.latitude, longitude: match.longitude });
                              setLocationSource("google_geocoder");
                              setDraft(current => ({ ...current, location: { ...current.location, address: match.address, installationAddress: match.address, addressDetails: { reference: suggestion.placeId, provenance: "google_places" }, pending: false } }));
                              setMapError("Selección de Google resuelta por placeId. Revisa el edificio y el pin antes de continuar.");
                            }).catch(() => {
                              setPin(null);
                              setDraft(current => ({ ...current, location: { ...current.location, address: suggestion.description, installationAddress: suggestion.description, pending: true } }));
                              setMapError("No se pudo resolver este placeId exacto con Google Geocoding. No se colocó un pin; usa Buscar dirección para candidatos explícitos o CartoCiudad si Google falla.");
                            }).finally(() => setAddressSearchPending(false));
                          }}
                        >
                          {suggestion.description}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null
              )}
              {supplyLocationSeed.ambiguous && !String(draft.location.address ?? "").trim() && <label className="grid max-w-2xl gap-1 text-sm">
                Este expediente tiene varias direcciones de suministro. Elige el emplazamiento del proyecto; no se seleccionará una automáticamente.
                <select aria-label="Dirección de suministro para el emplazamiento" className="h-11 rounded-md border bg-background px-3" value={supplyLocationSeed.choices.includes(String(draft.location.address ?? "")) ? String(draft.location.address) : ""} onChange={event => {
                  const selected = event.target.value;
                  if (!supplyLocationSeed.choices.includes(selected)) return;
                  geocodeRequest.current += 1;
                  setAddressSearchPending(false);
                  setChoices([]);
                  setResolvedCartoCandidate(null);
                  setMapError("");
                  setPin(null);
                  setLocationSource("manual");
                  setSelectedSuggestion("");
                  setDraft(current => ({ ...current, location: { ...current.location, address: selected, installationAddress: selected, addressDetails: undefined, pending: true } }));
                  startAutoResolveForAddress(selected);
                }}><option value="">Selecciona dirección del suministro</option>{supplyLocationSeed.choices.map((address, index) => <option key={`${index}:${address}`} value={address}>{address}</option>)}</select>
              </label>}
              <details className="max-w-3xl" open={editAddressDetails} onToggle={event => setEditAddressDetails((event.currentTarget as HTMLDetailsElement).open)}>
                <summary className="min-h-10 cursor-pointer py-2 text-sm font-medium">Editar detalles</summary>
                <p className="mb-2 text-xs text-muted-foreground">Componentes manuales para reutilización. No se derivan separando la dirección por comas.</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {([["street","Vía"],["streetNumber","Número"],["complement","Complemento"],["postalCode","Código postal"],["municipality","Municipio / población"],["province","Provincia"],["autonomousCommunity","Comunidad autónoma"],["country","País"]] as const).map(([field,label]) => <label key={field} className="grid gap-1 text-sm">{label}<Input controlSize="touch" aria-label={label} value={String(draft.location.addressDetails?.[field] ?? "")} onChange={event => setDraft(current => ({ ...current, location: { ...current.location, addressDetails: { ...(current.location.addressDetails ?? {}), [field]: event.target.value, provenance: "manual" } } }))}/></label>)}
                </div>
              </details>
              <Input controlSize="touch"
                placeholder="Dirección factura/postal si distinta"
                value={draft.location.billAddress ?? ""}
                onChange={e =>
                  setDraft(d => ({
                    ...d,
                    location: { ...d.location, billAddress: e.target.value },
                  }))
                }
              />
              {mapError && (
                <p role="status" className="text-sm text-amber-800">
                  {mapError}
                </p>
              )}
              {choices.map((choice: any) => (
                <button
                  type="button"
                  key={choice.placeId}
                  className="block w-full rounded-lg border p-3 text-left text-sm"
                  disabled={confirmingLocation || addressSearchPending}
                  onClick={() => {
                    if (confirmLocationInFlight.current || addressSearchPending) return;
                    void chooseAddressCandidate(choice);
                  }}
                >
                  {choice.address}
                </button>
              ))}
              {resolvedCartoCandidate && <div className="space-y-2 rounded-xl bg-muted/40 p-3">
                <p role="status" className="text-sm">¿Es esta la ubicación correcta? Propuesta: {resolvedCartoCandidate.address} · {resolvedCartoCandidate.latitude.toFixed(6)}, {resolvedCartoCandidate.longitude.toFixed(6)}{resolvedCartoCandidate.addressDetails?.precision ? ` · Precisión CartoCiudad: ${resolvedCartoCandidate.addressDetails.precision}` : ""}. El pin todavía no está colocado. Puedes moverlo antes de guardar.</p>
                <Button type="button" size="touch" disabled={confirmingLocation || addressSearchPending} onClick={confirmResolvedCartoCandidate}>Confirmar esta ubicación de CartoCiudad</Button>
              </div>}
              {roofMapActive && <Button type="button" size="touch" variant="outline" onClick={() => setRoofViewport(false)}>Volver al mapa satélite de ubicación</Button>}
              {draft.location.address && (
                <CommercialPinMap
                  value={pin}
                  previewCenter={resolvedCartoCandidate ? { latitude: resolvedCartoCandidate.latitude, longitude: resolvedCartoCandidate.longitude } : null}
                  onFallbackSearch={() => void searchCartoCiudadAddress()}
                  onChange={(nextPin, source) => {
                    if (!confirmLocationInFlight.current) {
                      setResolvedCartoCandidate(null);
                      setPin(nextPin);
                      setLocationSource(source ?? "google_maps");
                      setDraft(d => ({ ...d, location: { ...d.location, pending: false } }));
                    }
                  }}
                  disabled={confirmingLocation}
                  mapVisible={!roofMapActive}
                  pinInteractionEnabled={!roofMapActive}
                  onMapReadyShared={map => setCommercialGoogleMap(current => current === map ? current : map)}
                  onRoofModeRequest={() => setRoofViewport(true)}
                  googleMapChildren={(map, moveMapViewport) => draft.serviceType === "photovoltaic" ? <CommercialRoofSurvey
                    requestId={id ?? 0} version={version ?? 1} center={pin ? [pin.longitude, pin.latitude] : null} initial={request.data?.roofGeometry}
                    deferSave mapVisible={roofMapActive} googleMap={map} onGoogleMapViewportChange={moveMapViewport}
                    onSaveReady={save => { roofSaveRef.current = save; }} onMapModeChange={setRoofViewport}
                    onPinChange={point => { setPin({ latitude: point[1], longitude: point[0] }); setLocationSource("manual"); }}
                    onSaved={row => { const nextVersion = Number(row.version); setVersion(nextVersion); versionRef.current = nextVersion; }}
                  /> : null}
                />
              )}
              {roofMapActive && !commercialGoogleMap && draft.serviceType === "photovoltaic" && pin && <CommercialRoofSurvey
                requestId={id ?? 0} version={version ?? 1} center={[pin.longitude, pin.latitude]} initial={request.data?.roofGeometry}
                deferSave mapVisible googleMap={null}
                onSaveReady={save => { roofSaveRef.current = save; }} onMapModeChange={setRoofViewport}
                onPinChange={point => { setPin({ latitude: point[1], longitude: point[0] }); setLocationSource("manual"); }}
                onSaved={row => { const nextVersion = Number(row.version); setVersion(nextVersion); versionRef.current = nextVersion; }}
              />}
              {pin && <p role="status" className="text-sm text-muted-foreground">Pin propuesto · se confirmará y guardará al continuar.</p>}
              <label className="flex gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={Boolean(draft.location.pending)}
                  disabled={confirmingLocation}
                  onChange={e =>
                    setDraft(d => ({
                      ...d,
                      location: { ...d.location, pending: e.target.checked },
                    }))
                  }
                />
                Dejar ubicación pendiente para oficina
              </label>
              {locationHistoryEntries.length > 0 && <details className="rounded-xl bg-muted/30 p-3">
                <summary className="min-h-10 cursor-pointer py-2 text-sm font-medium">Cambios anteriores de ubicación · {locationHistoryEntries.length}</summary>
                <ol className="space-y-2 pt-2">{locationHistoryEntries.slice().reverse().map((entry: any) => <li key={entry.id} className="text-sm">
                  <span className="font-medium">{entry.after.address || "Dirección sin indicar"}</span>
                  <span className="ml-2 text-xs text-muted-foreground">{Number.isFinite(Number(entry.after.latitude)) && Number.isFinite(Number(entry.after.longitude)) ? `${Number(entry.after.latitude).toFixed(5)}, ${Number(entry.after.longitude).toFixed(5)}` : "coordenadas no acreditables"} · {entry.after.source} · {entry.actorName || "equipo autorizado"}{entry.createdAt ? ` · ${new Date(entry.createdAt).toLocaleString()}` : ""}</span>
                  {entry.before?.address && entry.before.address !== entry.after.address && <p className="text-xs text-muted-foreground">Antes: {entry.before.address}</p>}
                </li>)}</ol>
              </details>}

            </div>
          )}
          {activeStage === "photos" && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold">
                {draft.serviceType === "ev_charger"
                  ? "Fotos del punto de carga y su recorrido"
                  : draft.serviceType === "maintenance"
                    ? "Fotos de la instalación y la zona de actuación"
                    : "Fotos para preparar el presupuesto fotovoltaico"}
              </h2>
              <label className="grid gap-1 text-sm">
                Estado de fotografías
                <select
                  className="h-11 w-full min-w-0 max-w-full rounded-md border bg-background px-3 xl:h-10"
                  value={draft.need.photoStatus ?? "pending"}
                  onChange={e =>
                    setDraft(d => ({
                      ...d,
                      need: { ...d.need, photoStatus: e.target.value },
                    }))
                  }
                >
                  <option value="pending">Pendiente</option>
                  <option value="in_progress">Aportadas parcialmente</option>
                  <option value="not_applicable">No disponible en esta visita</option>
                </select>
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                {photoCategories.map(([category, label]) => (
                  <label
                    key={category}
                    className="rounded-xl border p-3 text-sm"
                  >
                    {label}
                    <span className="mt-2 block text-xs">
                      Hacer foto
                      <input
                        className="block w-full"
                        type="file"
                        accept="image/*"
                        capture="environment"
                        multiple
                        onChange={e => {
                          void upload(e.target.files, "supporting", category);
                          e.currentTarget.value = "";
                        }}
                      />
                    </span>
                    <span className="mt-2 block text-xs">
                      Elegir de galería/archivos
                      <input
                        className="block w-full"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={e => {
                          void upload(e.target.files, "supporting", category);
                          e.currentTarget.value = "";
                        }}
                      />
                    </span>
                  </label>
                ))}
              </div>
              <Textarea
                placeholder="Pendiente / no aplica / observaciones para oficina"
                value={draft.need.photoNotes ?? ""}
                onChange={e =>
                  setDraft(d => ({
                    ...d,
                    need: { ...d.need, photoNotes: e.target.value },
                  }))
                }
              />
              <CommercialDocumentManager
                requestId={id ?? 0}
                version={version ?? Number(request.data?.version ?? 1)}
                documents={docs.data ?? []}
                kind="supporting"
                categoryScope="photos"
                photoCategories={draft.need.photoCategories?.categories ?? {}}
                onPhotoCategoryChange={async (photoCategory, value) => {
                  const row: any = await savePhotoCategory.mutateAsync({ id: id!, version: versionRef.current ?? Number(request.data?.version ?? 1), category: photoCategory as any, value });
                  versionRef.current = Number(row.version);
                  setVersion(Number(row.version));
                  setDraft(current => ({ ...current, need: { ...current.need, photoCategories: row.photoCategories } }));
                }}
                onVersion={v => setVersion(v)}
                refresh={async () => {
                  await Promise.all([docs.refetch(), request.refetch()]);
                }}
                readOnly={!id}
              />
              {docs.data?.map((doc: any) => (
                <p key={doc.id} className="rounded border p-2 text-sm">
                  {COMMERCIAL_PHOTO_CATEGORY_LABELS[doc.captureCategory] ??
                    (doc.documentKind === "electricity_bill"
                      ? "Factura eléctrica"
                      : doc.documentKind === "clarification"
                        ? "Aclaración"
                        : "Sin categoría")}: {doc.fileName}
                </p>
              ))}
            </div>
          )}
          {activeStage === "summary" && (
            <div className="min-w-0 space-y-4 [overflow-wrap:anywhere]">
              <h2 className="text-xl font-semibold">
                Resumen y entrega a oficina
              </h2>
              <dl className="grid min-w-0 gap-2 sm:grid-cols-2">
                {[
                  [
                    "Servicio",
                    draft.serviceType
                      ? COMMERCIAL_SERVICE_LABELS[draft.serviceType] ?? "Pendiente"
                      : "Pendiente",
                  ],
                  [
                    draft.serviceType === "photovoltaic" ? "Objetivo fotovoltaico" : "Trabajo solicitado",
                    draft.serviceType === "photovoltaic" && draft.need.objective
                      ? COMMERCIAL_OBJECTIVE_LABELS[draft.need.objective] ?? "Pendiente"
                      : draft.serviceType === "photovoltaic"
                        ? "Pendiente"
                        : draft.serviceType ? "Ver descripción" : "Pendiente",
                  ],
                  [
                    "Cliente",
                    context.data?.customers?.find((customer: any) => Number(customer.id) === draft.customerPartnerId)?.tradeName
                      || context.data?.customers?.find((customer: any) => Number(customer.id) === draft.customerPartnerId)?.legalName
                      || draft.parties.clientName || "Cliente existente / pendiente",
                  ],
                  ["Sede canónica", context.data?.sites?.find((site: any) => Number(site.id) === draft.siteId)?.name || "Sin sede canónica seleccionada"],
                  ["Contactos canónicos", (draft.parties.contacts ?? []).map((contact: any) => `${contact.name}${contact.primary ? " (principal)" : ""}`).join(", ") || "Pendiente"],
                  ["Contacto", draft.parties.contacts?.find((contact: any) => contact.primary)?.name || (context.data?.contacts ?? []).find((contact: any) => Number(contact.id) === draft.contactId)?.displayName || draft.parties.contactName || "Pendiente"],
                  ["Teléfono", draft.parties.phone || "Pendiente"],
                  ["Email", draft.parties.email || "Pendiente"],
                  ["Titular", draft.parties.supplyHolderName || "Pendiente"],
                  [
                    "Contratante",
                    draft.parties.contractingPartyName || "Pendiente",
                  ],
                  ["Emplazamiento", draft.location.address || "Pendiente"],
                  ["Adjuntos", String(docs.data?.length ?? 0)],
                  [
                    "Facturas distintas",
                    String(
                      new Set(
                        (docs.data ?? [])
                          .filter(
                            (doc: any) =>
                              doc.documentKind === "electricity_bill"
                          )
                          .map(
                            (doc: any) => doc.captureGroupId || `doc-${doc.id}`
                          )
                      ).size
                    ),
                  ],
                  [
                    "Fotos",
                    draft.need.photoStatus === "not_applicable"
                      ? "No aplica"
                      : draft.need.photoStatus === "in_progress"
                        ? "Aportadas parcialmente"
                        : "Pendiente",
                  ],
                ].map(([k, v]) => (
                  <div key={k} className="min-w-0 rounded-lg border p-3 [overflow-wrap:anywhere]">
                    <dt className="text-xs text-muted-foreground">{k}</dt>
                    <dd className="font-medium [overflow-wrap:anywhere]">{v}</dd>
                  </div>
                ))}
              </dl>
              {draft.serviceType === "photovoltaic" && ["expand", "batteries"].includes(String(draft.need.objective)) && (
                <section aria-label="Resumen de instalación existente" className="space-y-2 rounded-xl border p-3">
                  <h3 className="font-medium">Instalación existente</h3>
                  <p className="text-sm">{draft.need.knownInstallationLabel || "Sin instalación vinculada; descripción manual"}</p>
                  <p className="text-sm">Paneles: {draft.need.existingPanels || "pendiente"} · Potencia: {draft.need.existingPower || "pendiente"} kWp · Inversor: {draft.need.existingInverter || "pendiente"} · Baterías: {draft.need.existingBatteries || "pendiente"}</p>
                  {draft.need.existingDescription && <p className="text-sm">Descripción: {draft.need.existingDescription}</p>}
                </section>
              )}
              <section aria-label="Documentos y fotos adjuntos" className="space-y-2 rounded-xl border p-3">
                <h3 className="font-medium">Documentos y fotos adjuntos</h3>
                {(docs.data ?? []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">Ningún archivo adjunto todavía.</p>
                ) : (
                  <ul className="list-disc space-y-1 pl-5 text-sm">
                    {(docs.data ?? []).map((doc: any) => (
                      <li key={doc.id}>{COMMERCIAL_PHOTO_CATEGORY_LABELS[doc.captureCategory] ?? (doc.documentKind === "electricity_bill" ? "Factura eléctrica" : doc.documentKind === "clarification" ? "Aclaración" : "Documento de apoyo")}: {doc.fileName}</li>
                    ))}
                  </ul>
                )}
              </section>
              {draft.serviceType === "ev_charger" && (
                <section aria-label="Resumen del cargador" className="rounded-xl border p-3">
                  <h3 className="font-medium">Datos del cargador</h3>
                  <p className="mt-1 text-sm">
                    Emplazamiento: {draft.need.siteKind === "single_house" ? "Unifamiliar" : draft.need.siteKind === "community" ? "Comunidad" : "Pendiente"} · Fase: {draft.need.phase === "mono" ? "Monofásica" : draft.need.phase === "tri" ? "Trifásica" : "Desconocida"}
                  </p>
                  <p className="text-sm">
                    Distancia aproximada: {String(draft.need.distanceMeters ?? "").trim() !== "" ? `${String(draft.need.distanceMeters).trim()} m` : "Pendiente"}
                    {draft.need.siteKind === "community" ? " · Los 70 m solo aplican si se incluyen expresamente en la oferta comunitaria validada." : " · El alcance requiere validación técnica."}
                  </p>
                </section>
              )}
              {holderContractorDifference && (
                <p
                  role="status"
                  className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950"
                >
                  El contratante y el titular del suministro son distintos.
                  Oficina debe revisar esta diferencia antes de contratación y
                  legalización. El interlocutor no determina esta comparación.
                </p>
              )}
              <section aria-label="Pauta y necesidades futuras" className="space-y-3 rounded-xl bg-muted/35 p-4">
                <h3 className="font-semibold">Uso y planificación</h3>
                <dl className="grid gap-3 sm:grid-cols-2">
                  <div><dt className="text-sm font-medium">Descripción de la necesidad</dt><dd className="mt-1 whitespace-pre-wrap break-words text-sm">{String(draft.need.description ?? "").trim() || "No indicado"}</dd></div>
                  <div><dt className="text-sm font-medium">Pauta de uso y consumo</dt><dd className="mt-1 text-sm">{formatConsumptionPattern(draft.need.consumptionPattern)}</dd></div>
                  {(Object.entries(FUTURE_NEED_LABELS) as Array<[keyof typeof FUTURE_NEED_LABELS, string]>).map(([key, label]) => <div key={key}><dt className="text-sm font-medium">{label}</dt><dd className="mt-1 text-sm">{formatFutureNeedStatus(draft.need.futureNeeds?.[key])}</dd></div>)}
                </dl>
              </section>
              <section
                className="space-y-2 rounded-xl border p-3"
                aria-label="Resumen de suministros eléctricos"
              >
                <h3 className="text-lg font-semibold">Resumen energético</h3>
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">{(draft.parties.supplies ?? []).map((supply: any, index: number) => <article key={`energy-summary-${index}`} className="rounded-xl bg-muted/35 p-4"><span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Suministro {index + 1}</span><p className="mt-1 font-medium">{supply.tariffLabel || supply.legacyTariffLabel || supply.tariff || "Tarifa pendiente"}</p><div className="mt-3 flex flex-wrap gap-2 text-xs"><span className="rounded-full bg-background px-2.5 py-1">{(supply.consumptionPeriods ?? []).length} lecturas de consumo</span><span className="rounded-full bg-background px-2.5 py-1">{(supply.contractedPower ?? []).length} periodos de potencia</span></div><p className="mt-2 text-xs text-muted-foreground">{supply.cups ? `CUPS ${supply.cups}` : "CUPS por completar"} · fase {supply.phase || "desconocida"}</p></article>)}</div>
                <h4 className="font-medium">Detalle técnico para oficina</h4>
                {(draft.parties.supplies ?? []).length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    Suministro pendiente de identificar; no se ha inventado
                    ningún valor.
                  </p>
                )}
                {(draft.parties.supplies ?? []).map(
                  (supply: any, index: number) => (
                    <div
                      key={index}
                      className="rounded-lg bg-muted/50 p-3 text-sm"
                    >
                      <b>Suministro {index + 1}</b> · CUPS{" "}
                      {supply.cups || "pendiente"} ·{" "}
                      {supply.retailer || "comercializadora pendiente"} ·
                      Distribuidora {supply.distributor || "pendiente"} · Tarifa{" "}
                      {supply.tariffLabel ||
                        supply.legacyTariffLabel ||
                        supply.tariff ||
                        "desconocida"}
                      <p className="mt-1 text-xs">
                        Potencias:{" "}
                        {(supply.contractedPower ?? [])
                          .map((r: any) => `${r.period} ${r.value} kW`)
                          .join(" · ") || "pendientes"}
                      </p>
                      <ul aria-label={`Lecturas de consumo del suministro ${index + 1}`} className="space-y-1 text-xs">
                        {(supply.consumptionPeriods ?? []).map((r: any, readingIndex: number) => (
                          <li key={`${readingIndex}-${r.period}-${r.value}`}>
                            Consumo {r.period}: {r.value} kWh{r.startDate || r.endDate ? ` · ${r.startDate ?? "fecha sin indicar"}–${r.endDate ?? "fecha sin indicar"}` : " · intervalo no indicado"}
                            {r.source?.page ? ` · pág. ${r.source.page}` : ""}
                            {r.reviewRequired ? ` · ${Array.isArray(r.reviewReasons) && r.reviewReasons.includes("period_overlap") ? "solape con otra lectura del mismo periodo; revisar antes de comparar" : "intervalo ambiguo o incompleto; indicar fechas antes de comparar"}` : ""}
                          </li>
                        ))}
                        {!supply.consumptionPeriods?.length && <li>Consumos pendientes</li>}
                      </ul>
                      <p className="text-xs">
                        Fase {supply.phase ?? "desconocida"} · Tensión {supply.connectionVoltage ?? "desconocida"}
                      </p>
                    </div>
                  )
                )}
              </section>
              <p className="rounded-lg bg-muted p-3 text-sm">
                Pendientes técnicos visibles para oficina; el envío no completa
                ni modifica automáticamente la ficha maestra.
              </p>
              {pendingItems.length > 0 && (
                <section
                  aria-label="Datos pendientes"
                  className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950"
                >
                  <h3 className="font-medium">Pendiente para oficina</h3>
                  <ul className="list-disc space-y-1 pl-5">
                    {pendingItems.map(item => <li key={item}>{item}</li>)}
                  </ul>
                </section>
              )}
              {confirmPendingSend && pendingItems.length > 0 && (
                <section
                  aria-label="Confirmar envío con datos pendientes"
                  className="space-y-3 rounded-lg border p-3"
                >
                  <p className="text-sm">
                    Confirma que quieres enviar la solicitud a oficina con los
                    pendientes indicados. No se enviará ningún email al cliente.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={() => setConfirmPendingSend(false)}>
                      Volver al resumen
                    </Button>
                    <Button disabled={saving || submitting} onClick={() => void submit()}>
                      Confirmar envío a oficina
                    </Button>
                  </div>
                </section>
              )}
            </div>
          )}
        </section>
        {activeStage === "details" && contactGateMessage && <p role="alert" className="text-sm text-destructive">{contactGateMessage}</p>}
        <footer data-testid="wizard-action-footer" className={`commercial-touch-flow flex w-full min-w-0 max-w-full flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between ${activeStage === "need" ? "commercial-need-footer" : ""}`}>
          {activeStage === "summary" && !requiredFieldsReady && (
            <p role="status" className="w-full text-sm text-muted-foreground">
              Para enviar, indica teléfono y email válidos, identifica al cliente o a un interlocutor y completa servicio, necesidad y dirección o deja la ubicación pendiente para oficina.
            </p>
          )}
          {!readOnlyOfficeReview && <Button
            variant="outline"
            onClick={() => goToPriorStep(Math.max(0, step - 1))}
            disabled={step === 0}
            className="commercial-flow-action w-full min-w-0 max-w-full whitespace-normal leading-tight sm:w-auto sm:whitespace-nowrap"
          >
            Anterior
          </Button>}
          <div className="commercial-flow-actions flex w-full min-w-0 max-w-full flex-col gap-2 sm:w-auto sm:flex-row">
            {!readOnlyOfficeReview && (step < activeStages.length - 1 ? (
              <Button disabled={saving || confirmingLocation || applyBillBatch.isPending} onClick={() => void next()} className={`commercial-flow-action commercial-primary-action w-full min-w-0 max-w-full whitespace-normal leading-tight sm:w-auto sm:whitespace-nowrap ${activeStage === "need" ? "commercial-need-cta" : ""}`} >
                Siguiente
              </Button>
            ) : pendingItems.length > 0 ? (
              <Button
                disabled={
                  saving ||
                  !requiredFieldsReady ||
                  confirmPendingSend
                }
                onClick={() => setConfirmPendingSend(true)}
                className="commercial-flow-action commercial-primary-action w-full min-w-0 max-w-full whitespace-normal leading-tight sm:w-auto sm:whitespace-nowrap"
              >
                Enviar con datos pendientes
              </Button>
            ) : (
              <Button
                disabled={saving || submitting || !requiredFieldsReady}
                onClick={() => void submit()}
                className="commercial-flow-action commercial-primary-action w-full min-w-0 max-w-full whitespace-normal leading-tight sm:w-auto sm:whitespace-nowrap"
              >
                Enviar a oficina
              </Button>
            ))}
          </div>
        </footer>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
      </main>
    </CommercialPresentationFrame>
  );
}
